import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";

import { BillerOperatorService } from "../biller-operator.service";
import { RazorpayTransactionService } from "src/app/PayLater/razorpay/razorpayTransactionService.service";
import {
  PaymentGatewayInitializeRequestDTO,
  PaymentGatewayTransactionCreateRequestDTO,
  PaymentGatewayTransactionUpdateRequestDTO,
} from "src/app/PayLater/Payment-Gateway/paymentGateway.service";
import { PaymentGatewayService } from "src/app/PayLater/Payment-Gateway/paymentGateway.interface";
import { UserLoginService } from "src/app/login/userlogin.service";
import {SupportTicketCreateRequestDTO} from "src/app/BBPS/end-user-bbps/support-ticket/support-ticket.interface";
import { SupportTicketService } from "src/app/BBPS/end-user-bbps/support-ticket/support-ticket.service";

import {
  BbpsBillApiResponseDTO,
  BbpsBillDetailDTO,
  BbpsAccountHistoryApiResponseDTO,
  BbpsAccountHistoryRequestDTO,
  BbpsFetchBillRequestDTO,
  BbpsOperatorApiResponseDTO,
  BbpsOperatorItemDTO,
  BbpsOperatorRequestDTO,
  BillerInputFieldDTO,
  BillerOperatorCategoryDTO,
  BillerOperatorSelectionDTO,
  BillerOperatorStep,
  BillerPaymentModeDTO,
  GetMobileOperatorRequestDTO,
  MobileOperatorApiResponseDTO,
  MobileOperatorResponseDTO,
  NormalizedMobileOperatorDTO,
  PrepaidPlanApiResponseDTO,
  PrepaidPlanByNameRequestDTO,
  PrepaidPlanRequestDTO,
  PrepaidPlanViewDTO,
} from "../biller-operator-interface";

interface BbpsResolvedPaymentReference {
  masterId: number;
  prrn: string;
  billFetchId: string;
  masterType: "BBPS" | "RECHARGE";
}

interface BbpsBillDisplayRow {
  key: string;
  label: string;
  value: string;
}

type BbpsTransactionHistoryStatusKind =
  | "success"
  | "failed"
  | "cancelled"
  | "processing";

interface BbpsTransactionHistoryItem {
  id: string;
  mobileNumber: string;
  operatorName: string;
  amount: number;
  status: string;
  statusKind: BbpsTransactionHistoryStatusKind;
  transactionDate: string;
  orderId: string;
  prrn: string;
  rechargeId: string;
  rawData: any;
}

interface PaymentFailureDialogData {
  customerName: string;
  mobile: string;
  email: string;

  serviceName: string;
  providerName: string;
  customerReference: string;
  billerType: string;

  amount: number;
  referenceId: number | null;
  transactionId: string;
  orderId: string;
  prrn: string;

  failureReason: string;
}

@Component({
  selector: "app-biller-operator",
  templateUrl: "./biller-operator.component.html",
  styleUrls: ["./biller-operator.component.css"],
})
export class BillerOperatorComponent
  implements OnChanges, OnDestroy
{
  /** Logged-in session fallback, matching the working SafeGold flow. */
  private readonly loggedInUser = inject(UserLoginService).User();
  private readonly paymentGatewayService = inject(PaymentGatewayService);
  private readonly supportTicketService =inject(SupportTicketService);

  @Input() open = false;

  @Input()
  category: BillerOperatorCategoryDTO | null = null;

  @Input() userId = 0;

  /**
   * Optional values from the parent component. Existing screens do not need
   * to pass these when FetchBill / InitiateRecharge already returns them.
   */
  @Input() paymentMasterId = 0;
  @Input() paymentPrrn = "";
  @Input() paymentBillFetchId = "";
  @Input() paymentActionUser = "";
  @Input() autoRefreshAfterPayment = true;

  @Output() closed = new EventEmitter<void>();

  @Output()
  completed =
    new EventEmitter<BillerOperatorSelectionDTO>();

  /**
   * Additional non-breaking event for screens that want the raw Razorpay
   * success response. The existing completed event remains unchanged.
   */
  @Output() paymentSucceeded = new EventEmitter<any>();

  step: BillerOperatorStep = "customer-details";

  loading = false;
  loadingMessage = "Please wait";
  submitted = false;
  errorMessage = "";
  informationMessage = "";

  fields: BillerInputFieldDTO[] = [];
  formData: Record<string, string> = {};

  operatorDetails: NormalizedMobileOperatorDTO | null =
    null;

  providerList: BbpsOperatorItemDTO[] = [];
  selectedProviderCode = "";
  providerLoading = false;

  /**
   * Non-mobile BBPS categories first display a searchable
   * provider list. After selection, the existing customer
   * details and bill-fetch flow continues unchanged.
   */
  providerStage:
    | "provider-list"
    | "customer-form" =
    "provider-list";

  providerSearch = "";

  prepaidPlans: PrepaidPlanViewDTO[] = [];
  selectedPlan: PrepaidPlanViewDTO | null = null;
  selectedPlanCategory = "";
  planSearch = "";
  planRequestInProgress = false;

  fetchedBill: BbpsBillDetailDTO | null = null;

  /**
   * Customer-facing rows generated dynamically from the complete FetchBill
   * response. This preserves provider-specific fields without hard-coding
   * Electricity, DTH, Gas, Water, FASTag, Broadband, EMI, etc.
   */
  billDetailRows: BbpsBillDisplayRow[] = [];

  /**
   * Keep the complete API envelopes because requestId, prrn and billFetchId
   * are often returned beside the normalized bill data rather than inside it.
   */
  private fetchedBillApiResponse: BbpsBillApiResponseDTO | null = null;
  private rechargeInitializationResponse: any = null;

  amount: number | null = null;
  selectedPaymentMode = "";

  paymentProcessing = false;
  paymentSuccess = false;
  paymentStatusMessage = "";

  paymentTimerSeconds = 600;
  paymentTimerExpired = false;

  /**
   * API-backed transaction history displayed inside the same biller modal.
   * It is a normal view, not another popup, so the existing payment flow and
   * Razorpay lifecycle remain unchanged.
   */
  transactionHistoryOpen = false;
  transactionHistoryLoading = false;
  transactionHistoryMessage = "";
  transactionHistory: BbpsTransactionHistoryItem[] = [];
  private transactionHistoryLoaded = false;

  private razorpaySubscription: any = null;
  private paymentRefreshTimer: any = null;
  private razorpayDismissedByUser = false;

  /**
   * Razorpay Checkout is opened by the shared payment service. Some versions
   * of that service replace modal.ondismiss, so this component also observes
   * the checkout DOM, browser focus and Observable completion.
   */
  private razorpayDismissMonitor: any = null;
  private razorpayCheckoutObserver: MutationObserver | null = null;
  private razorpayDismissSafetyTimer: any = null;
  private razorpayCheckoutWasVisible = false;
  private razorpayWindowWasBlurred = false;
  private razorpayCheckoutStartedAt = 0;
  private razorpayMissingCheckCount = 0;
  private paymentCountdownInterval: any = null;
  private paymentCountdownExpiresAt = 0;

  private readonly razorpayWindowBlurHandler = (): void => {
    if (this.paymentProcessing && !this.paymentSuccess) {
      this.razorpayWindowWasBlurred = true;
    }
  };

  private readonly razorpayWindowFocusHandler = (): void => {
    if (!this.paymentProcessing || this.paymentSuccess) {
      return;
    }

    /*
     * Popup-mode Razorpay gives focus back to this page after the customer
     * closes checkout. Wait briefly so Razorpay can remove its DOM first.
     */
    setTimeout(() => {
      if (
        this.paymentProcessing &&
        !this.paymentSuccess &&
        this.razorpayWindowWasBlurred &&
        !this.isRazorpayCheckoutVisible()
      ) {
        this.handleRazorpayDismissed();
      }
    }, 450);
  };

  private readonly razorpayVisibilityChangeHandler = (): void => {
    if (
      document.visibilityState === "visible" &&
      this.paymentProcessing &&
      !this.paymentSuccess
    ) {
      this.razorpayWindowFocusHandler();
    }
  };

  /**
   * Non-prepaid billers normally return requestId/PRRN from FetchBill.
   * When a provider supports direct recharge/payment and does not return those
   * values, a pending gateway transaction is created dynamically and its
   * paymentId becomes the secure Razorpay masterId.
   */
  private fallbackPaymentIntentId = 0;
  private fallbackPaymentIntentPrrn = "";

  /**
   * Prevents the same category from initializing more than once
   * when Angular receives open and category changes together.
   */
  private popupInitializationKey = "";
  paymentFailed = false;
  failedTicketData: SupportTicketCreateRequestDTO | null = null;
  paymentFailureDialogData:PaymentFailureDialogData | null = null;
  showPaymentFailureDialog = false;
  showPaymentFailureDetails = false;
  querySubmitting = false;
  queryRaised = false;
  queryErrorMessage = "";
  createdQueryTicketNo = "";
  paymentDialogMode:| "success"| "failure"| null = null;
  

  readonly paymentModes: BillerPaymentModeDTO[] = [
    {
      id: "upi",
      name: "UPI",
      description: "Pay using any UPI application",
      icon: "fa-solid fa-mobile-screen-button",
    },
    {
      id: "card",
      name: "Debit / Credit Card",
      description: "Visa, Mastercard, RuPay and other cards",
      icon: "fa-regular fa-credit-card",
    },
    {
      id: "netbanking",
      name: "Net Banking",
      description: "Pay securely through your bank",
      icon: "fa-solid fa-building-columns",
    },
    {
      id: "wallet",
      name: "Wallet",
      description: "Pay using your available wallet balance",
      icon: "fa-solid fa-wallet",
    },
  ];

  constructor(
    private readonly billerOperatorService: BillerOperatorService,
    private readonly razorpayTransactionService: RazorpayTransactionService,
    private readonly ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.open) {
      const currentCategoryKey = [
        this.category?.categoryId ?? "",
        this.category?.name ?? "",
      ].join("|");

      const openedNow =
        changes["open"]?.currentValue === true &&
        changes["open"]?.previousValue !== true;

      const categoryChanged =
        !!changes["category"] &&
        currentCategoryKey !==
          this.popupInitializationKey;

      if (openedNow || categoryChanged) {
        this.popupInitializationKey =
          currentCategoryKey;

        this.initializePopup();
      }
    }

    if (
      changes["open"] &&
      changes["open"].currentValue === false
    ) {
      this.popupInitializationKey = "";
      this.restorePageScroll();
    }
  }

  ngOnDestroy(): void {
    this.stopPaymentCountdown();
    this.clearPaymentRefreshTimer();
    this.clearRazorpayDismissMonitor();
    this.unsubscribeRazorpayRequest();
    this.restorePageScroll();
  }

  @HostListener("document:keydown.escape")
  onEscapePressed(): void {
    if (this.open && !this.paymentProcessing) {
      this.closePopup();
    }
  }

  get categoryName(): string {
    return this.category?.name?.trim() || "Bill Payment";
  }

  get categoryDescription(): string {
    return (
      this.category?.category?.trim() ||
      "Enter the required payment information."
    );
  }

  get supportsPlanSelection(): boolean {
    const name = this.categoryName
      .toLowerCase()
      .trim();

    return (
      name === "prepaid" ||
      name.includes("mobile prepaid") ||
      name.includes("datacard prepaid")
    );
  }

  get isPrepaidCategory(): boolean {
    const name = this.categoryName.toLowerCase();

    return (
      name === "prepaid" ||
      name.includes("mobile prepaid") ||
      name.includes("datacard prepaid")
    );
  }

  get isCreditCardCategory(): boolean {
    return this.categoryName
      .toLowerCase()
      .includes("credit card");
  }

  get isMobileOperatorCategory(): boolean {
    const name = this.categoryName.toLowerCase();

    return (
      name.includes("prepaid") ||
      name.includes("postpaid") ||
      name.includes("datacard")
    );
  }

  /**
   * Existing getter name retained.
   */
  get isOperatorLookupRequired(): boolean {
    return this.isMobileOperatorCategory;
  }

  get usesExternalProviderList(): boolean {
    return !this.isMobileOperatorCategory;
  }

  get selectedProvider():
    | BbpsOperatorItemDTO
    | null {
    if (!this.selectedProviderCode) {
      return null;
    }

    return (
      this.providerList.find(
        (provider) =>
          provider.code === this.selectedProviderCode,
      ) ?? null
    );
  }

  get isProviderSelectionStage(): boolean {
    return (
      this.step === "customer-details" &&
      this.usesExternalProviderList &&
      this.providerStage === "provider-list"
    );
  }

  get isProviderCustomerFormStage(): boolean {
    return (
      this.step === "customer-details" &&
      this.usesExternalProviderList &&
      this.providerStage === "customer-form"
    );
  }

  get filteredProviders():
    BbpsOperatorItemDTO[] {
    const search = this.providerSearch
      .trim()
      .toLowerCase();

    if (!search) {
      return this.providerList;
    }

    return this.providerList.filter(
      (provider) => {
        const searchableText = [
          provider.name,
          provider.code,
          provider.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(search);
      },
    );
  }

  get customerReference(): string {
    const firstRequiredField =
      this.fields.find((field) => field.required) ??
      this.fields[0];

    if (!firstRequiredField) {
      return "";
    }

    return String(
      this.formData[firstRequiredField.key] ?? "",
    ).trim();
  }

  get mobileNumber(): string {
    return String(
      this.formData["mobileNumber"] ??
        this.formData["accountNumber"] ??
        "",
    ).trim();
  }

  get currentAmount(): number {
    return Number(this.amount ?? 0);
  }

  get planCategories(): string[] {
    const categories = Array.from(
      new Set(
        this.prepaidPlans
          .map((plan) => plan.category)
          .filter(Boolean),
      ),
    );

    const recommendedIndex = categories.findIndex(
      (category) =>
        category.toLowerCase() === "recommended",
    );

    if (recommendedIndex > 0) {
      const [recommended] = categories.splice(
        recommendedIndex,
        1,
      );

      categories.unshift(recommended);
    }

    return categories;
  }

  get filteredPlans(): PrepaidPlanViewDTO[] {
    const search = this.planSearch
      .trim()
      .toLowerCase();

    return this.prepaidPlans.filter((plan) => {
      const matchesCategory =
        !this.selectedPlanCategory ||
        plan.category === this.selectedPlanCategory;

      const matchesSearch =
        !search ||
        String(plan.amount).includes(search) ||
        plan.validity.toLowerCase().includes(search) ||
        plan.dataBenefit.toLowerCase().includes(search) ||
        plan.description.toLowerCase().includes(search) ||
        plan.talkTime.toLowerCase().includes(search);

      return matchesCategory && matchesSearch;
    });
  }

  private initializePopup(): void {
    this.syncLoggedInUserContext();

    this.step = "customer-details";
    this.loading = false;
    this.loadingMessage = "Please wait";
    this.submitted = false;
    this.errorMessage = "";
    this.informationMessage = "";

    this.operatorDetails = null;

    this.providerList = [];
    this.selectedProviderCode = "";
    this.providerLoading = false;
    this.providerSearch = "";

    this.providerStage =
      this.usesExternalProviderList
        ? "provider-list"
        : "customer-form";

    this.prepaidPlans = [];
    this.selectedPlan = null;
    this.selectedPlanCategory = "";
    this.planSearch = "";
    this.planRequestInProgress = false;

    this.fetchedBill = null;
    this.billDetailRows = [];
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();

    this.amount = null;
    this.selectedPaymentMode = "";

    this.clearPaymentRefreshTimer();
    this.clearRazorpayDismissMonitor();
    this.unsubscribeRazorpayRequest();
    this.paymentProcessing = false;
    this.paymentSuccess = false;
    this.paymentStatusMessage = "";
    this.razorpayDismissedByUser = false;

    this.transactionHistoryOpen = false;
    this.transactionHistoryLoading = false;
    this.transactionHistoryMessage = "";
    this.transactionHistory = [];
    this.transactionHistoryLoaded = false;
    this.paymentFailed = false;
    this.failedTicketData = null;
    this.paymentFailureDialogData = null;

    this.showPaymentFailureDialog = false;
    this.showPaymentFailureDetails = false;
    this.paymentDialogMode = null;
    this.querySubmitting = false;
    this.queryRaised = false;
    this.queryErrorMessage = "";
    this.createdQueryTicketNo = "";
    this.fields = this.getFieldsForCategory();
    this.formData = {};

    this.fields.forEach((field) => {
      this.formData[field.key] = "";
    });

    document.body.style.overflow = "hidden";

    if (this.usesExternalProviderList) {
      this.loadProvidersForCategory();
    }
  }

  private restorePageScroll(): void {
    document.body.style.overflow = "";
  }

  closePopup(): void {
    if (this.paymentProcessing) {
      return;
    }

    this.stopPaymentCountdown();
    this.clearPaymentRefreshTimer();
    this.clearRazorpayDismissMonitor();
    this.unsubscribeRazorpayRequest();
    this.restorePageScroll();
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  onProviderChanged(): void {
    this.errorMessage = "";
    this.informationMessage = "";
    this.fetchedBill = null;
    this.billDetailRows = [];
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();
    this.amount = null;
  }

  selectProvider(
    provider: BbpsOperatorItemDTO,
  ): void {
    this.selectedProviderCode =
      provider.code;

    this.providerStage =
      "customer-form";

    this.submitted = false;
    this.errorMessage = "";
    this.informationMessage = "";
    this.fetchedBill = null;
    this.billDetailRows = [];
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();
    this.amount = null;
  }

  editSelectedProvider(): void {
    this.providerStage =
      "provider-list";

    this.submitted = false;
    this.errorMessage = "";
    this.informationMessage = "";
    this.fetchedBill = null;
    this.billDetailRows = [];
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();
    this.amount = null;
  }

  submitCustomerDetails(): void {
    this.submitted = true;
    this.errorMessage = "";
    this.informationMessage = "";

    if (
      this.usesExternalProviderList &&
      this.providerStage ===
        "provider-list"
    ) {
      this.errorMessage =
        "Please select a service provider first.";
      return;
    }

    if (
      this.usesExternalProviderList &&
      !this.selectedProviderCode
    ) {
      this.errorMessage =
        "Please select a service provider.";
      return;
    }

    if (!this.validateFields()) {
      return;
    }

    if (this.isMobileOperatorCategory) {
      this.fetchMobileOperator();
      return;
    }

    this.fetchCategoryBill();
  }

  private fetchMobileOperator(): void {
    if (!this.mobileNumber) {
      this.errorMessage =
        "Please enter a valid mobile or datacard number.";
      return;
    }

    const resolvedUserId = this.resolveLoggedInUserId();

    const payload: GetMobileOperatorRequestDTO = {
      mobileNumber: this.mobileNumber,
    };

    if (resolvedUserId > 0) {
      payload.userId = resolvedUserId;
    }

    this.loading = true;
    this.loadingMessage =
      "Fetching operator details";

    this.errorMessage = "";
    this.informationMessage = "";

    this.billerOperatorService
      .BBPS_GetMobileOperator(payload)
      .subscribe({
        next: (
          response: MobileOperatorApiResponseDTO,
        ) => {
          const normalized =
            this.normalizeOperatorResponse(
              response,
            );

          if (!normalized.operatorName) {
            this.loading = false;

            this.errorMessage =
              normalized.errorMessage ||
              "Operator details could not be detected. Please verify the number.";

            return;
          }

          this.operatorDetails = normalized;
          this.submitted = false;

          /*
           * Only prepaid categories load recharge plans.
           * Postpaid keeps the existing review/amount flow.
           */
          if (this.supportsPlanSelection) {
            this.fetchPrepaidPlans();
            return;
          }

          this.loading = false;
          this.step = "review";
        },

        error: (error: any) => {
          this.loading = false;

          this.errorMessage =
            this.getApiErrorMessage(error);
        },
      });
  }

  private fetchPrepaidPlans(): void {
    if (this.planRequestInProgress) {
      return;
    }

    /*
     * Backend now uses PrepaidPlanByNameRequestDTO:
     * {
     *   OperatorName,
     *   CircleName
     * }
     */
    const operatorName =
      this.operatorDetails?.operatorName
        ?.trim() ?? "";

    const circleName =
      this.operatorDetails?.circleName
        ?.trim() ?? "";

    if (!operatorName) {
      this.loading = false;
      this.step = "review";

      this.informationMessage =
        "Operator name was not returned. Please detect the operator again.";

      return;
    }

    if (!circleName) {
      this.loading = false;
      this.step = "review";

      this.informationMessage =
        "Circle name was not returned. Please detect the operator again.";

      return;
    }

    const payload:
      PrepaidPlanByNameRequestDTO = {
        operatorName,
        circleName,
      };

    this.planRequestInProgress = true;
    this.loading = true;
    this.loadingMessage =
      "Fetching available recharge plans";

    this.errorMessage = "";
    this.informationMessage = "";

    this.prepaidPlans = [];
    this.selectedPlan = null;
    this.selectedPlanCategory = "";
    this.planSearch = "";
    this.amount = null;

    this.billerOperatorService
      .BBPS_GetPrepaidPlanByName(payload)
      .subscribe({
        next: (
          response: PrepaidPlanApiResponseDTO,
        ) => {
          this.planRequestInProgress = false;

          this.prepaidPlans =
            this.normalizePrepaidPlans(
              response,
            );

          this.selectedPlanCategory =
            this.planCategories[0] ?? "";

          this.loading = false;
          this.step = "review";

          if (
            this.prepaidPlans.length === 0
          ) {
            const apiMessage =
              response?.message ??
              response?.Message ??
              "";

            this.informationMessage =
              apiMessage
                ? `${apiMessage} No recognizable plan records were found in the response payload.`
                : "No recharge plans were returned for this operator and circle. Please try again shortly.";
          }
        },

        error: (error: any) => {
          this.planRequestInProgress = false;
          this.prepaidPlans = [];
          this.selectedPlan = null;
          this.selectedPlanCategory = "";

          this.loading = false;
          this.step = "review";

          this.informationMessage =
            this.getApiErrorMessage(error) ||
            "Recharge plans are temporarily unavailable. Please try again shortly.";
        },
      });
  }

  loadProvidersForCategory(): void {
    if (!this.category || this.providerLoading) {
      return;
    }

    const categoryId = Number(
      this.category.categoryId,
    );

    if (
      !Number.isFinite(categoryId) ||
      categoryId <= 0
    ) {
      this.providerList = [];
      this.informationMessage =
        "A valid category ID is required to load providers.";
      return;
    }

    const payload: BbpsOperatorRequestDTO = {
      category: categoryId,
    };

    this.providerLoading = true;
    this.errorMessage = "";
    this.informationMessage = "";

    const request$ = this.isCreditCardCategory
      ? this.billerOperatorService
          .BBPS_GetCreditCardOperators(payload)
      : this.billerOperatorService
          .BBPS_GetBillerOperators(payload);

    request$.subscribe({
      next: (
        response: BbpsOperatorApiResponseDTO,
      ) => {
        /*
         * Credit-card GetOperatorList returns plain JSON in this shape:
         *
         * {
         *   msg: "Operator Fetched.",
         *   data: [
         *     {
         *       oprCode: "21461",
         *       category: "Credit Card Payments",
         *       operatorName: "AU Bank Credit Card"
         *     }
         *   ]
         * }
         *
         * The response has already reached Angular successfully. The provider
         * mapper below handles this plain data array as well as the existing
         * encrypted/decrypted BBPS response shapes.
         */
        this.providerList =
          this.normalizeProviderList(response);

        this.providerLoading = false;

        if (this.providerList.length === 0) {
          const responseObject =
            response as any;

          this.informationMessage =
            responseObject?.msg ??
            responseObject?.Msg ??
            response?.message ??
            response?.Message ??
            "No providers were returned for this category.";
        }
      },

      error: (error: any) => {
        this.providerLoading = false;
        this.providerList = [];

        this.informationMessage =
          this.getApiErrorMessage(error) ||
          "Unable to load service providers. Please try again.";
      },
    });
  }

  private fetchCategoryBill(): void {
    if (!this.category) {
      this.errorMessage =
        "Bill category details are unavailable.";
      return;
    }

    const provider = this.selectedProvider;

    const providerRaw =
      provider?.rawData as any;

    const billerCode = String(
      providerRaw?.spKey ??
      providerRaw?.SpKey ??
      providerRaw?.SPKey ??
      providerRaw?.spkey ??
      providerRaw?.SPKEY ??
      providerRaw?.serviceProviderKey ??
      providerRaw?.ServiceProviderKey ??
      providerRaw?.operatorKey ??
      providerRaw?.OperatorKey ??
      providerRaw?.billerCode ??
      providerRaw?.BillerCode ??
      providerRaw?.oprCode ??
      providerRaw?.OprCode ??
      providerRaw?.OPRCode ??
      providerRaw?.operatorCode ??
      providerRaw?.OperatorCode ??
      provider?.code ??
      "",
    ).trim();

    if (!billerCode) {
      this.errorMessage =
        "Please select a valid service provider.";
      return;
    }

    const enteredCustomerReference =
      this.customerReference;

    const customerNumber =
      this.normalizeCustomerReferenceForFetchBill(
        enteredCustomerReference,
      );

    const sessionMobile =
      this.getSessionMobileForBbps();

    const subCategoryId =
      this.getProviderSubCategoryId();

    if (sessionMobile.length !== 10) {
      this.errorMessage =
        "Logged-in mobile number is unavailable. Please sign in again before fetching the bill.";
      return;
    }

    if (
      !Number.isFinite(subCategoryId) ||
      subCategoryId <= 0
    ) {
      this.errorMessage =
        "Selected provider sub-category ID is unavailable. Please select the provider again.";
      return;
    }

    /*
     * Keep every existing web field and add the exact FetchBill contract used
     * by BBPS/BLR/FetchBill:
     *
     * {
     *   customerNumber,
     *   billerCode,
     *   mobile,
     *   subCategoryId
     * }
     *
     * Sending both shapes is backward-compatible with the existing Angular
     * DTO while allowing the API to receive its real bill-fetch parameters.
     */
    const payload:
      BbpsFetchBillRequestDTO &
      Record<string, any> = {
        categoryId: this.category.categoryId,
        categoryName: this.category.name,
        operatorCode: billerCode,
        billerCode,
        customerReference:
          enteredCustomerReference,
        customerParameters: {
          ...this.formData,
          customerNumber,
          mobile: sessionMobile,
          subCategoryId: String(subCategoryId),
        },

        customerNumber,
        mobile: sessionMobile,
        subCategoryId,
      };

    const resolvedUserId =
      this.resolveLoggedInUserId();

    if (resolvedUserId > 0) {
      payload.userId = resolvedUserId;
    }

    this.loading = true;
    this.loadingMessage =
      this.isCreditCardCategory
        ? "Fetching credit-card bill"
        : "Fetching bill details";

    this.errorMessage = "";
    this.informationMessage = "";
    this.fetchedBill = null;
    this.billDetailRows = [];
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();
    this.amount = null;

    const request$ =
      this.isCreditCardCategory
        ? this.billerOperatorService
            .BBPS_GetCreditCardBill(payload)
        : this.billerOperatorService
            .BBPS_FetchBill(payload);

    console.log(
      "BBPS FetchBill request:",
      {
        category: this.categoryName,
        customerNumber,
        billerCode,
        mobile: sessionMobile,
        subCategoryId,
      },
    );

    request$.subscribe({
      next: (
        response: BbpsBillApiResponseDTO,
      ) => {
        console.log(
          "BBPS FetchBill response:",
          response,
        );
        /*
         * Retain the complete response. requestId, PRRN and billFetchId can be
         * at root/data/responseJson while the customer bill can be nested at
         * data.data. The recursive parser below supports all of these shapes.
         */
        this.fetchedBillApiResponse =
          response;

        const fetchSucceeded =
          this.isFetchBillResponseSuccessful(
            response,
          );

        const normalizedBill =
          this.normalizeFetchedBill(response);

        const detailRows =
          this.buildBillDisplayRows(
            response,
            normalizedBill,
          );

        if (
          !fetchSucceeded ||
          !normalizedBill ||
          detailRows.length === 0
        ) {
          this.loading = false;
          this.submitted = false;
          this.fetchedBill = null;
          this.billDetailRows = [];
          this.fetchedBillApiResponse = null;
          this.amount = null;

          this.errorMessage =
            this.getFetchBillFailureMessage(
              response,
              "Bill details were not returned. Please verify the entered customer details and try again.",
            );

          /*
           * Stay on the customer-details screen. Do not allow payment to
           * bypass FetchBill when the provider did not validate the account.
           */
          this.step = "customer-details";
          return;
        }

        this.fetchedBill =
          normalizedBill;

        this.billDetailRows =
          detailRows;

        if (
          normalizedBill.amount > 0
        ) {
          this.amount =
            normalizedBill.amount;
        }

        this.loading = false;
        this.step = "review";
        this.submitted = false;

        if (this.shouldShowPaymentTimer) {
        this.startPaymentCountdown();
        }

        if (
          normalizedBill.amount <= 0
        ) {
          this.informationMessage =
            "Bill details were fetched successfully, but the provider did not return a payable amount. Enter the amount to continue.";
        } else {
          this.informationMessage =
            "Bill fetched successfully. Review the details and continue to Razorpay.";
        }
      },

      error: (error: any) => {
        this.loading = false;
        this.submitted = false;
        this.fetchedBill = null;
        this.billDetailRows = [];
        this.fetchedBillApiResponse = null;
        this.amount = null;

        /*
         * Stay on the form so the user can correct the Consumer Number /
         * Customer ID and call FetchBill again.
         */
        this.step = "customer-details";

        this.errorMessage =
          this.getFetchBillHttpErrorMessage(error);
      },
    });
  }


  /**
   * Opens the transaction history inside the existing biller modal.
   * No additional dialog is created.
   */
  openTransactionHistory(): void {
    if (!this.supportsPlanSelection) {
      return;
    }

    this.transactionHistoryOpen = true;
    this.errorMessage = "";
    this.informationMessage = "";

    if (!this.transactionHistoryLoaded) {
      this.loadRechargeTransactionHistory();
    }
  }

  closeTransactionHistory(): void {
    this.transactionHistoryOpen = false;
    this.transactionHistoryMessage = "";
  }

  refreshTransactionHistory(): void {
    this.loadRechargeTransactionHistory(true);
  }

  /**
   * Calls POST /BBPS/BFR/AccountHistory through BillerOperatorService.
   * The mapper accepts the current API envelope as well as nested encrypted
   * response shapes without changing the existing recharge/payment APIs.
   */
  private loadRechargeTransactionHistory(force = false): void {
    if (this.transactionHistoryLoading) {
      return;
    }

    if (this.transactionHistoryLoaded && !force) {
      return;
    }

    const userId = this.resolveLoggedInUserId();

    if (!Number.isFinite(userId) || userId <= 0) {
      this.transactionHistory = [];
      this.transactionHistoryLoaded = false;
      this.transactionHistoryMessage =
        "User session is unavailable. Please sign in again.";
      return;
    }

    this.transactionHistoryLoading = true;
    this.transactionHistoryMessage = "";

    const payload: BbpsAccountHistoryRequestDTO = {
      userId,
    };

    this.billerOperatorService
      .BBPS_AccountHistory(payload)
      .subscribe({
        next: (
          response: BbpsAccountHistoryApiResponseDTO,
        ) => {
          this.transactionHistory =
            this.normalizeTransactionHistory(response);
          this.transactionHistoryLoaded = true;
          this.transactionHistoryLoading = false;

          if (this.transactionHistory.length === 0) {
            this.transactionHistoryMessage =
              "No recharge transactions were found for this account.";
          }

          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        },

        error: (error: any) => {
          this.transactionHistory = [];
          this.transactionHistoryLoaded = false;
          this.transactionHistoryLoading = false;
          this.transactionHistoryMessage =
            this.getApiErrorMessage(error) ||
            "Unable to load transaction history right now.";

          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  private normalizeTransactionHistory(
    response: any,
  ): BbpsTransactionHistoryItem[] {
    const candidates: any[] = [];

    this.collectTransactionHistoryCandidates(
      response,
      candidates,
      0,
      new Set<any>(),
    );

    const currentCategoryId = Number(
      this.category?.categoryId || 0,
    );

    const uniqueItems =
      new Map<string, BbpsTransactionHistoryItem>();

    candidates.forEach((candidate, index) => {
      const parsedCandidate =
        this.parsePossibleJson(candidate);

      if (
        !parsedCandidate ||
        typeof parsedCandidate !== "object" ||
        Array.isArray(parsedCandidate) ||
        !this.isRechargeHistoryCandidate(
          parsedCandidate,
          currentCategoryId,
        )
      ) {
        return;
      }

      const mobileNumber = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "mobileNumber",
            "mobileNo",
            "mobile",
            "consumerNumber",
            "customerNumber",
            "customerReference",
            "account",
          ],
        ) || "",
      )
        .replace(/\D/g, "")
        .slice(-10);

      const operatorName = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "operatorName",
            "providerName",
            "billerName",
            "serviceProviderName",
            "operator",
            "provider",
          ],
        ) || "Recharge",
      ).trim();

      const amount = this.readNumericValue(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "amount",
            "requestedAmount",
            "paidAmount",
            "payAmount",
            "clientPaidAmount",
            "transactionAmount",
            "rechargeAmount",
          ],
        ),
      );

      const rawStatus = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "transactionStatus",
            "rechargeStatus",
            "paymentStatus",
            "apiStatus",
            "orderStatus",
            "status",
          ],
        ) || "PROCESSING",
      ).trim();

      const transactionDate = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "transactionDate",
            "paymentDate",
            "createdOn",
            "createdAt",
            "modifiedOn",
            "date",
            "timestamp",
          ],
        ) || "",
      ).trim();

      const orderId = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "gatewayOrderId",
            "razorpayOrderId",
            "orderId",
            "orderid",
          ],
        ) || "",
      ).trim();

      const prrn = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "prrn",
            "paymentReferenceNumber",
            "referenceNumber",
          ],
        ) || "",
      ).trim();

      const rechargeId = String(
        this.findFirstValueByAliases(
          [parsedCandidate],
          [
            "rechargeId",
            "transactionId",
            "paymentId",
            "requestId",
            "billFetchId",
            "id",
          ],
        ) || "",
      ).trim();

      const id =
        rechargeId ||
        orderId ||
        prrn ||
        [
          mobileNumber,
          amount,
          transactionDate,
          index,
        ].join("|");

      const item: BbpsTransactionHistoryItem = {
        id,
        mobileNumber,
        operatorName,
        amount,
        status: this.getTransactionHistoryStatusLabel(
          rawStatus,
        ),
        statusKind:
          this.resolveTransactionHistoryStatusKind(
            rawStatus,
          ),
        transactionDate,
        orderId,
        prrn,
        rechargeId,
        rawData: parsedCandidate,
      };

      const uniqueKey = [
        item.rechargeId,
        item.orderId,
        item.prrn,
        item.mobileNumber,
        item.amount,
        item.transactionDate,
      ].join("|");

      if (!uniqueItems.has(uniqueKey)) {
        uniqueItems.set(uniqueKey, item);
      }
    });

    return Array.from(uniqueItems.values()).sort(
      (left, right) =>
        this.getTransactionHistoryTimestamp(
          right.transactionDate,
        ) -
        this.getTransactionHistoryTimestamp(
          left.transactionDate,
        ),
    );
  }

  private collectTransactionHistoryCandidates(
    value: any,
    output: any[],
    depth: number,
    visited: Set<any>,
  ): void {
    if (
      value === null ||
      value === undefined ||
      depth > 10
    ) {
      return;
    }

    const parsedValue = this.parsePossibleJson(value);

    if (
      parsedValue &&
      typeof parsedValue === "object"
    ) {
      if (visited.has(parsedValue)) {
        return;
      }

      visited.add(parsedValue);
    }

    if (Array.isArray(parsedValue)) {
      parsedValue.forEach((item) => {
        const parsedItem = this.parsePossibleJson(item);

        if (this.looksLikeTransactionHistoryItem(parsedItem)) {
          output.push(parsedItem);
        } else {
          this.collectTransactionHistoryCandidates(
            parsedItem,
            output,
            depth + 1,
            visited,
          );
        }
      });

      return;
    }

    if (
      !parsedValue ||
      typeof parsedValue !== "object"
    ) {
      return;
    }

    if (this.looksLikeTransactionHistoryItem(parsedValue)) {
      output.push(parsedValue);
      return;
    }

    Object.values(parsedValue).forEach((childValue) => {
      this.collectTransactionHistoryCandidates(
        childValue,
        output,
        depth + 1,
        visited,
      );
    });
  }

  private looksLikeTransactionHistoryItem(value: any): boolean {
    if (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value)
    ) {
      return false;
    }

    const hasReference =
      this.findFirstValueByAliases(
        [value],
        [
          "rechargeId",
          "transactionId",
          "paymentId",
          "gatewayOrderId",
          "orderId",
          "prrn",
        ],
      ) !== null;

    const hasTransactionData =
      this.findFirstValueByAliases(
        [value],
        [
          "amount",
          "requestedAmount",
          "rechargeAmount",
          "status",
          "paymentStatus",
          "transactionDate",
          "createdOn",
        ],
      ) !== null;

    return hasReference && hasTransactionData;
  }

  private isRechargeHistoryCandidate(
    value: any,
    currentCategoryId: number,
  ): boolean {
    const categoryId = Number(
      this.findFirstValueByAliases(
        [value],
        ["categoryId", "billerCategoryId"],
      ) || 0,
    );

    if (
      categoryId > 0 &&
      currentCategoryId > 0
    ) {
      return categoryId === currentCategoryId;
    }

    const moduleType = String(
      this.findFirstValueByAliases(
        [value],
        [
          "moduleType",
          "moduleName",
          "transactionType",
          "paymentFor",
        ],
      ) || "",
    )
      .trim()
      .toUpperCase();

    if (moduleType) {
      return (
        moduleType.includes("RECHARGE") ||
        moduleType.includes("PREPAID")
      );
    }

    return (
      this.findFirstValueByAliases(
        [value],
        [
          "rechargeId",
          "mobileNumber",
          "operatorCode",
          "operatorName",
        ],
      ) !== null
    );
  }

  private resolveTransactionHistoryStatusKind(
    value: any,
  ): BbpsTransactionHistoryStatusKind {
    const status = String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[\s_-]+/g, "");

    if (
      [
        "SUCCESS",
        "SUCCESSFUL",
        "PAID",
        "COMPLETED",
        "COMPLETE",
        "CONFIRMED",
        "APPROVED",
      ].includes(status)
    ) {
      return "success";
    }

    if (
      [
        "CANCELLED",
        "CANCELED",
        "USERCANCELLED",
        "USERCANCELED",
      ].includes(status)
    ) {
      return "cancelled";
    }

    if (
      [
        "FAILED",
        "FAILURE",
        "REJECTED",
        "DECLINED",
        "ERROR",
        "TIMEOUT",
        "TIMEDOUT",
        "EXPIRED",
      ].includes(status)
    ) {
      return "failed";
    }

    return "processing";
  }

  private getTransactionHistoryStatusLabel(
    value: any,
  ): string {
    const statusKind =
      this.resolveTransactionHistoryStatusKind(value);

    if (statusKind === "success") {
      return "SUCCESS";
    }

    if (statusKind === "failed") {
      return "FAILED";
    }

    if (statusKind === "cancelled") {
      return "CANCELLED";
    }

    return "PROCESSING";
  }

  getTransactionHistoryStatusIcon(
    item: BbpsTransactionHistoryItem,
  ): string {
    if (item.statusKind === "success") {
      return "fa-solid fa-circle-check";
    }

    if (item.statusKind === "failed") {
      return "fa-solid fa-circle-xmark";
    }

    if (item.statusKind === "cancelled") {
      return "fa-solid fa-ban";
    }

    return "fa-regular fa-clock";
  }

  getTransactionHistoryDisplayTime(
    value: string,
  ): string {
    const timestamp =
      this.getTransactionHistoryTimestamp(value);

    if (!timestamp) {
      return "Time unavailable";
    }

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(new Date(timestamp));
  }

  private getTransactionHistoryTimestamp(
    value: string,
  ): number {
    const timestamp = Date.parse(String(value || ""));
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  trackByTransactionHistoryId(
    index: number,
    item: BbpsTransactionHistoryItem,
  ): string {
    return item.id || String(index);
  }


  selectPlan(plan: PrepaidPlanViewDTO): void {
    this.selectedPlan = plan;
    this.amount = plan.amount;
    this.errorMessage = "";
  }

  onAmountChanged(value: number | null): void {
    this.amount =
      value === null ||
      value === undefined ||
      value === ("" as any)
        ? null
        : Number(value);

    if (
      this.selectedPlan &&
      Number(this.amount) !==
        Number(this.selectedPlan.amount)
    ) {
      this.selectedPlan = null;
    }
  }

  proceedToPaymentMode(): void {
    this.openDirectRazorpayCheckout();
  }

  /**
   * Opens Razorpay directly from the review screen.
   * Razorpay displays every payment method enabled for the merchant account.
   */
openDirectRazorpayCheckout(): void {
  this.errorMessage = "";
  this.informationMessage = "";
  this.paymentStatusMessage = "";
  this.showPaymentFailureDialog = false;
  this.showPaymentFailureDetails = false;
  this.paymentDialogMode = null;
  this.querySubmitting = false;
  this.queryRaised = false;
  this.queryErrorMessage = "";
  this.createdQueryTicketNo = ""; 
  this.paymentFailed = false;
  this.failedTicketData = null;
  this.paymentFailureDialogData = null;

  if (
    this.paymentProcessing ||
    this.paymentSuccess
  ) {
    return;
  }

    if (!this.category) {
      this.errorMessage =
        "Bill category details are unavailable.";
      return;
    }

    if (
      this.supportsPlanSelection &&
      !this.selectedPlan
    ) {
      this.errorMessage =
        "Please select a recharge plan.";
      return;
    }

  const numericAmount = Number(
    this.amount ?? 0,
  );

  if (
    !Number.isFinite(numericAmount) ||
    numericAmount <= 0
  ) {
    this.errorMessage =
      this.supportsPlanSelection
        ? "Please select a recharge plan."
        : "Please enter a valid payment amount.";

    return;
  }

  this.amount = numericAmount;

  /*
   * Remove any previous failure before a new payment attempt.
   */

  if (this.shouldShowPaymentTimer) {
  this.stopPaymentCountdown(false);
}

  this.selectedPaymentMode = "razorpay";

  const result =
    this.buildBillerOperatorSelectionResult(
      numericAmount,
    );

  this.paymentProcessing = true;
  this.paymentSuccess = false;
  this.razorpayDismissedByUser = false;

  this.paymentStatusMessage =
    this.supportsPlanSelection
      ? "Creating recharge payment reference"
      : "Preparing secure Razorpay checkout";

  this.resolvePaymentReference(
    result,
    numericAmount,
  )
    .then((paymentReference) => {
      this.openRazorpayCheckout(
        result,
        paymentReference,
        numericAmount,
      );
    })
    .catch((error: any) => {
      /*
       * This is a payment-reference preparation failure,
       * not an actual Razorpay payment failure.
       * Therefore, do not show Raise Ticket here.
       */
      this.paymentProcessing = false;
      this.paymentSuccess = false;
      this.paymentStatusMessage = "";

      this.errorMessage =
        this.getRazorpayErrorMessage(error);
    });
}

  /** Retained for compatibility with any older caller. */
  confirmPaymentMode(): void {
    this.openDirectRazorpayCheckout();
  }


  private buildBillerOperatorSelectionResult(
    numericAmount: number,
  ): BillerOperatorSelectionDTO {
    const provider = this.selectedProvider;

    return {
      userId: this.resolveLoggedInUserId(),
      categoryId: this.category!.categoryId,
      categoryName: this.category!.name,
      categoryDescription: this.category!.category,

      customerReference: this.customerReference,
      customerParameters: {
        ...this.formData,
      },

      providerId: provider?.id,
      providerName: provider?.name,
      providerCode: provider?.code,

      operatorName:
        this.operatorDetails?.operatorName || undefined,

      operatorCode:
        this.operatorDetails?.operatorCode || undefined,

      circleName:
        this.operatorDetails?.circleName || undefined,

      circleCode:
        this.operatorDetails?.circleCode || undefined,

      selectedPlan:
        this.selectedPlan || undefined,

      fetchedBill:
        this.fetchedBill || undefined,

      amount: numericAmount,
      paymentMode: this.selectedPaymentMode,
    };
  }

  private async resolvePaymentReference(
    result: BillerOperatorSelectionDTO,
    numericAmount: number,
  ): Promise<BbpsResolvedPaymentReference> {
    if (this.supportsPlanSelection) {
      /*
       * A prepaid payment intent must be created immediately before payment.
       * The returned rechargeId is the payment-gateway masterId; transactionId
       * is a different reference and must not replace rechargeId.
       */
      const initiationResponse =
        await this.tryInitiateRechargeReference(
          result,
          numericAmount,
        );

      this.rechargeInitializationResponse =
        initiationResponse;

      const rechargeSources = [
        initiationResponse,
      ];

      const masterId = Number(
        this.findFirstValueByAliases(
          rechargeSources,
          ["rechargeId"],
        ) || 0,
      );

      const prrn = String(
        this.findFirstValueByAliases(
          rechargeSources,
          [
            "prrn",
            "paymentReferenceNumber",
            "paymentReference",
            "referenceNumber",
            "referenceNo",
          ],
        ) || "",
      ).trim();

      const billFetchId = String(
        this.findFirstValueByAliases(
          rechargeSources,
          [
            "billFetchId",
            "fetchBillId",
            "fetchId",
            "billReferenceId",
          ],
        ) || "",
      ).trim();

      if (
        !Number.isFinite(masterId) ||
        masterId <= 0 ||
        !prrn
      ) {
        throw new Error(
          "Recharge reference was not generated. Please try again.",
        );
      }

      return {
        masterId,
        prrn,
        billFetchId,
        masterType: "RECHARGE",
      };
    }

    const sources = this.getPaymentReferenceSources();

    const masterId = Number(
      this.paymentMasterId ||
      this.findFirstValueByAliases(
        sources,
        [
          "requestId",
          "bbpsRequestId",
          "paymentRequestId",
          "masterId",
          "paymentMasterId",
          "transactionId",
          "paymentId",
        ],
      ) ||
      0,
    );

    const prrn = String(
      this.paymentPrrn ||
      this.findFirstValueByAliases(
        sources,
        [
          "prrn",
          "paymentReferenceNumber",
          "paymentReference",
          "referenceNumber",
          "referenceNo",
          "transactionReference",
          "merchantReference",
        ],
      ) ||
      "",
    ).trim();

    const billFetchId = String(
      this.paymentBillFetchId ||
      this.findFirstValueByAliases(
        sources,
        [
          "billFetchId",
          "fetchBillId",
          "fetchId",
          "billReferenceId",
          "billFetchReference",
        ],
      ) ||
      "",
    ).trim();

    /*
     * billFetchId is intentionally optional for opening Razorpay. Some direct
     * recharge providers return requestId + PRRN but no separate billFetchId.
     * The existing fetch response is still retained and passed in notes when
     * the provider returns it.
     */
    if (
      Number.isFinite(masterId) &&
      masterId > 0 &&
      prrn
    ) {
      return {
        masterId,
        prrn,
        billFetchId,
        masterType: "BBPS",
      };
    }

    /*
     * Direct-pay providers (for example some DTH, cable, voucher, FASTag and
     * similar operators) may validate the customer but not return a classic
     * BBPS requestId/PRRN. Create a real pending gateway-intent record through
     * the existing PaymentGatewayTransaction API instead of blocking Razorpay.
     */
    return this.createFallbackPaymentReference(
      result,
      numericAmount,
      billFetchId,
    );
  }

  private createFallbackPaymentReference(
    result: BillerOperatorSelectionDTO,
    numericAmount: number,
    existingBillFetchId: string,
  ): Promise<BbpsResolvedPaymentReference> {
    if (
      this.fallbackPaymentIntentId > 0 &&
      this.fallbackPaymentIntentPrrn
    ) {
      return Promise.resolve({
        masterId: this.fallbackPaymentIntentId,
        prrn: this.fallbackPaymentIntentPrrn,
        billFetchId: existingBillFetchId,
        masterType: "BBPS",
      });
    }

    const userId = this.resolveLoggedInUserId();

    if (!Number.isFinite(userId) || userId <= 0) {
      return Promise.reject(
        new Error(
          "User session is unavailable. Please sign in again.",
        ),
      );
    }

    const prrn = this.buildDynamicBbpsPrrn(userId);
    const businessMasterId = this.getFallbackBusinessMasterId();

    const payload: PaymentGatewayTransactionCreateRequestDTO = {
      moduleName: "BBPS",
      moduleId: businessMasterId,
      gatewayName: "RAZORPAY",

      /*
       * Temporary unique placeholders are replaced with the real Razorpay
       * payment/order IDs after successful checkout.
       */
      gatewayTransactionId: prrn,
      gatewayOrderId: prrn,

      paymentFor: `${this.categoryName} Payment Intent`,
      requestedUserId: userId,
      requestedAmount: numericAmount,
      clientPaidAmount: 0,
      gatewayFeeAmount: 0,
      disburseAmount: 0,
      paymentStatus: "Initiated",
      currencyCode: "INR",
      isRefunded: 0,
      refundAmount: 0,

      otherDetail1: "Selected in Razorpay Checkout",
      otherDetail2: "Web",
      otherDetail3: this.categoryName,
      otherDetail4: this.customerReference,
      otherDetail5:
        existingBillFetchId ||
        this.selectedProvider?.code ||
        "",
      otherDetail6: prrn,
      actionUser: this.resolvePaymentActionUser(),
    };

    this.paymentStatusMessage =
      "Creating secure payment reference";

    return new Promise<BbpsResolvedPaymentReference>(
      (resolve, reject) => {
        this.paymentGatewayService
          .PaymentGatewayTransaction_Create(payload)
          .subscribe({
            next: (response: any) => {
              const paymentId = Number(
                this.findFirstValueByAliases(
                  [response],
                  [
                    "paymentId",
                    "transactionId",
                    "paymentGatewayTransactionId",
                    "id",
                  ],
                ) || 0,
              );

              if (
                !Number.isFinite(paymentId) ||
                paymentId <= 0
              ) {
                reject(
                  new Error(
                    "Secure payment reference was not generated. Please try again.",
                  ),
                );
                return;
              }

              this.fallbackPaymentIntentId = paymentId;
              this.fallbackPaymentIntentPrrn = prrn;

              Object.assign(result as any, {
                paymentIntentId: paymentId,
                prrn,
                masterId: paymentId,
                masterType: "BBPS",
                billFetchId: existingBillFetchId,
              });

              resolve({
                masterId: paymentId,
                prrn,
                billFetchId: existingBillFetchId,
                masterType: "BBPS",
              });
            },

            error: (error: any) => {
              reject(
                new Error(
                  this.getApiErrorMessage(error) ||
                  "Unable to create the secure payment reference. Please try again.",
                ),
              );
            },
          });
      },
    );
  }

  private buildDynamicBbpsPrrn(userId: number): string {
    const categoryId = String(
      this.category?.categoryId || "0",
    ).replace(/\D/g, "");

    const timestamp = Date.now().toString();
    const userPart = String(userId).slice(-6);

    return `BBPS${categoryId}${userPart}${timestamp}`;
  }

  private getFallbackBusinessMasterId(): number {
    const providerId = Number(
      this.selectedProvider?.id || 0,
    );

    if (
      Number.isFinite(providerId) &&
      providerId > 0
    ) {
      return providerId;
    }

    const categoryId = Number(
      this.category?.categoryId || 0,
    );

    if (
      Number.isFinite(categoryId) &&
      categoryId > 0
    ) {
      return categoryId;
    }

    return this.resolveLoggedInUserId();
  }

  private resetFallbackPaymentIntent(): void {
    this.fallbackPaymentIntentId = 0;
    this.fallbackPaymentIntentPrrn = "";
  }

  private tryInitiateRechargeReference(
    result: BillerOperatorSelectionDTO,
    numericAmount: number,
  ): Promise<any> {
    const service = this.billerOperatorService as any;

    /*
     * BBPS_InitiateRecharge is the exact web equivalent of the working mobile
     * /BBPS/REH/InitiateRecharge flow. Older aliases remain accepted only to
     * avoid breaking projects where the existing method has another name.
     */
    const initiateMethod =
      service.BBPS_InitiateRecharge ||
      service.BBPS_InitializeRecharge ||
      service.BBPS_InitiatePaymentGatewayRecharge;

    if (typeof initiateMethod !== "function") {
      return Promise.reject(
        new Error(
          "BBPS recharge initialization API is not configured in BillerOperatorService.",
        ),
      );
    }

    const userId = this.resolveLoggedInUserId();
    const mobileNumber = this.mobileNumber
      .replace(/\D/g, "")
      .slice(-10);
    const operatorCode = String(
      result.operatorCode ||
      this.operatorDetails?.operatorCode ||
      "",
    ).trim();

    if (!Number.isFinite(userId) || userId <= 0) {
      return Promise.reject(
        new Error(
          "User session is unavailable. Please sign in again.",
        ),
      );
    }

    if (mobileNumber.length !== 10) {
      return Promise.reject(
        new Error(
          "Please enter a valid 10-digit mobile number.",
        ),
      );
    }

    if (!operatorCode) {
      return Promise.reject(
        new Error(
          "Operator code is missing. Please detect the operator again.",
        ),
      );
    }

    const payload = {
      userId,
      amount: numericAmount,
      mobileNumber,
      operatorCode,
    };

    return new Promise<any>((resolve, reject) => {
      let settled = false;

      const finishSuccess = (value: any): void => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(value);
      };

      const finishError = (error: any): void => {
        if (settled) {
          return;
        }

        settled = true;
        reject(
          new Error(
            this.getApiErrorMessage(error) ||
            "Unable to create the recharge payment reference. Please try again.",
          ),
        );
      };

      try {
        const request$ = initiateMethod.call(
          service,
          payload,
        );

        if (!request$?.subscribe) {
          finishError(
            new Error(
              "Recharge initialization API did not return a valid request.",
            ),
          );
          return;
        }

        let subscription: any = null;

        subscription = request$.subscribe({
          next: (response: any) => {
            subscription?.unsubscribe?.();
            finishSuccess(response);
          },
          error: (error: any) => {
            subscription?.unsubscribe?.();
            finishError(error);
          },
        });
      } catch (error: any) {
        finishError(error);
      }
    });
  }


  /**
   * Resets the internal payment overlay when Razorpay is closed without a
   * successful payment. The reset is idempotent and safe when it is triggered
   * by modal.ondismiss, Observable completion, DOM removal or browser focus.
   */
  private handleRazorpayDismissed(): void {
    this.ngZone.run(() => {
      if (
        this.paymentSuccess ||
        this.razorpayDismissedByUser
      ) {
        return;
      }

      this.razorpayDismissedByUser = true;
      this.paymentProcessing = false;
      this.paymentSuccess = false;
      this.paymentStatusMessage = "";
      this.errorMessage = "";
      this.informationMessage =
        "Payment was cancelled. No amount was charged. You can try again.";

      this.clearPaymentRefreshTimer();
      this.clearRazorpayDismissMonitor();
      this.unsubscribeRazorpayRequest();

      /*
       * detectChanges handles Razorpay callbacks that execute outside Angular.
       * markForCheck keeps this compatible with OnPush parent components.
       */
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
    });
  }

  /**
   * Returns true only while a visible Razorpay Checkout element exists.
   * The selector list supports both iframe and popup-style checkout versions.
   */
  private isRazorpayCheckoutVisible(): boolean {
    const selectors = [
      ".razorpay-container",
      ".razorpay-backdrop",
      "iframe.razorpay-checkout-frame",
      'iframe[src*="razorpay"]',
      'iframe[name*="razorpay"]',
      '[class*="razorpay-container"]',
      '[class*="razorpay-backdrop"]',
      '[data-testid*="razorpay"]',
      'form[action*="razorpay"]',
    ];

    const elements = Array.from(
      document.querySelectorAll<HTMLElement>(
        selectors.join(","),
      ),
    );

    return elements.some((element) => {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();

      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        Number(style.opacity || "1") !== 0 &&
        rect.width > 0 &&
        rect.height > 0
      );
    });
  }

  /**
   * Watches Razorpay independently from the shared service. This is required
   * because older RazorpayTransactionService implementations overwrite the
   * supplied modal object and never call this component's ondismiss callback.
   */
  private startRazorpayDismissMonitor(): void {
    this.clearRazorpayDismissMonitor();

    this.razorpayCheckoutWasVisible = false;
    this.razorpayWindowWasBlurred = false;
    this.razorpayCheckoutStartedAt = Date.now();
    this.razorpayMissingCheckCount = 0;

    window.addEventListener(
      "blur",
      this.razorpayWindowBlurHandler,
    );

    window.addEventListener(
      "focus",
      this.razorpayWindowFocusHandler,
    );

    document.addEventListener(
      "visibilitychange",
      this.razorpayVisibilityChangeHandler,
    );

    const inspectCheckoutState = (): void => {
      if (
        !this.paymentProcessing ||
        this.paymentSuccess ||
        this.razorpayDismissedByUser
      ) {
        return;
      }

      const checkoutVisible =
        this.isRazorpayCheckoutVisible();

      if (checkoutVisible) {
        this.razorpayCheckoutWasVisible = true;
        this.razorpayMissingCheckCount = 0;
        return;
      }

      if (this.razorpayCheckoutWasVisible) {
        this.razorpayMissingCheckCount += 1;

        /*
         * Require two consecutive missing checks so temporary Razorpay iframe
         * re-renders do not look like a cancellation.
         */
        if (this.razorpayMissingCheckCount >= 2) {
          this.handleRazorpayDismissed();
        }
      }
    };

    this.razorpayCheckoutObserver =
      new MutationObserver(() => {
        inspectCheckoutState();
      });

    if (document.body) {
      this.razorpayCheckoutObserver.observe(
        document.body,
        {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            "class",
            "style",
            "src",
          ],
        },
      );
    }

    this.razorpayDismissMonitor = setInterval(
      inspectCheckoutState,
      300,
    );

    /*
     * Final defensive reset. It is intentionally not ten minutes: if checkout
     * never opened, or its callbacks were lost, the UI becomes usable again.
     * The timer will not cancel an active visible checkout.
     */
    this.razorpayDismissSafetyTimer = setTimeout(() => {
      if (
        this.paymentProcessing &&
        !this.paymentSuccess &&
        !this.isRazorpayCheckoutVisible()
      ) {
        this.handleRazorpayDismissed();
      }
    }, 45_000);
  }

  private clearRazorpayDismissMonitor(): void {
    if (this.razorpayDismissMonitor) {
      clearInterval(this.razorpayDismissMonitor);
      this.razorpayDismissMonitor = null;
    }

    if (this.razorpayDismissSafetyTimer) {
      clearTimeout(this.razorpayDismissSafetyTimer);
      this.razorpayDismissSafetyTimer = null;
    }

    if (this.razorpayCheckoutObserver) {
      this.razorpayCheckoutObserver.disconnect();
      this.razorpayCheckoutObserver = null;
    }

    window.removeEventListener(
      "blur",
      this.razorpayWindowBlurHandler,
    );

    window.removeEventListener(
      "focus",
      this.razorpayWindowFocusHandler,
    );

    document.removeEventListener(
      "visibilitychange",
      this.razorpayVisibilityChangeHandler,
    );

    this.razorpayCheckoutWasVisible = false;
    this.razorpayWindowWasBlurred = false;
    this.razorpayCheckoutStartedAt = 0;
    this.razorpayMissingCheckCount = 0;
  }


private openRazorpayCheckout(
  result: BillerOperatorSelectionDTO,
  paymentReference: BbpsResolvedPaymentReference,
  numericAmount: number,
): void {
  const actionUser =
    this.resolvePaymentActionUser();

  const payload:
    PaymentGatewayInitializeRequestDTO = {
      prrn: paymentReference.prrn,
      masterType: paymentReference.masterType,
      masterId: paymentReference.masterId,
      amount: numericAmount,
      currency: "INR",
      gatewayName: "RAZORPAY",

      notes: {
        notes_key_1:
          paymentReference.masterType === "RECHARGE"
            ? "BBPS_RECHARGE"
            : "BBPS",

        notes_key_2:
          `${this.categoryName} - Razorpay Checkout`,
      },

      actionUser,
    };

  const razorpayOptions: any = {
    name: "Nishwik",

    description:
      `${this.categoryName} • ₹${numericAmount.toFixed(2)}`,

    prefill: {
      name: this.getRazorpayCustomerName(),
      email: this.getRazorpayCustomerEmail(),
      contact: this.getRazorpayCustomerMobile(),
    },

    theme: {
      color: "#011044",
    },

    notes: {
      payment_source: "RAZORPAY_CHECKOUT",

      category_id:
        String(this.category?.categoryId ?? ""),

      category_name:
        this.categoryName,

      customer_reference:
        this.customerReference,

      provider_code:
        this.selectedProvider?.code ?? "",

      provider_name:
        this.selectedProvider?.name ?? "",

      operator_code:
        this.operatorDetails?.operatorCode ?? "",

      operator_name:
        this.operatorDetails?.operatorName ?? "",

      master_type:
        paymentReference.masterType,

      master_id:
        String(paymentReference.masterId),

      bill_fetch_id:
        paymentReference.billFetchId,

      prrn:
        paymentReference.prrn,
    },

    modal: {
      escape: true,
      backdropclose: false,
      confirm_close: true,

      ondismiss: () => {
        this.razorpayDismissedByUser = true;

        this.paymentProcessing = false;
        this.paymentSuccess = false;
        this.paymentStatusMessage = "";

        /*
         * User cancellation is not a payment failure.
         * Therefore, do not show Raise Ticket.
         */
        this.showPaymentFailureDialog = false;
        this.showPaymentFailureDetails = false;
        this.paymentDialogMode = null;

        this.paymentFailed = false;
        this.failedTicketData = null;
        this.paymentFailureDialogData = null;

        this.querySubmitting = false;
        this.queryRaised = false;
        this.queryErrorMessage = "";
        this.createdQueryTicketNo = "";

        this.informationMessage =
          "Payment was cancelled. No bill or recharge request was submitted.";
      },
    },
  };

  this.unsubscribeRazorpayRequest();
    this.clearRazorpayDismissMonitor();

  this.paymentStatusMessage =
    "Opening secure Razorpay checkout";

      this.startRazorpayDismissMonitor();

  this.razorpaySubscription =
    this.razorpayTransactionService
      .InitializePaymentGateway(
        payload,
        razorpayOptions,
      )
      .subscribe({
        next: (paymentResponse: any) => {
              this.ngZone.run(() => {
                this.clearRazorpayDismissMonitor();
            this.unsubscribeRazorpayRequest();

                if (this.razorpayDismissedByUser) {
                  return;
                }

            this.completeRazorpayPayment(
              result,
              paymentReference,
              paymentResponse,
            );

                this.changeDetectorRef.detectChanges();
              });
        },

error: (error: any) => {
              this.ngZone.run(() => {
                this.clearRazorpayDismissMonitor();
    this.unsubscribeRazorpayRequest();

    /*
   * Closing Razorpay manually is cancellation,
   * not payment failure.
   */
  if (this.razorpayDismissedByUser) {
                  this.paymentProcessing = false;
                  this.paymentSuccess = false;
                  this.paymentStatusMessage = "";
                  this.changeDetectorRef.detectChanges();
      return;
    }

  const failureMessage =
    this.getRazorpayErrorMessage(error);

  const orderId = String(
    error?.error?.metadata?.order_id ??
    error?.error?.metadata?.orderId ??
    error?.error?.orderId ??
    error?.orderId ??
    "",
  ).trim();

  const transactionId = String(
    error?.error?.metadata?.payment_id ??
    error?.error?.metadata?.paymentId ??
    error?.error?.paymentId ??
    error?.paymentId ??
    "",
  ).trim();

  const billerType =
    paymentReference.masterType === "RECHARGE"
      ? "RECHARGE"
      : this.isCreditCardCategory
        ? "CREDITCARD"
        : "BBPS";

  const customerName =
    this.getRazorpayCustomerName();

  const customerMobile =
    this.getRazorpayCustomerMobile();

  const customerEmail =
    this.getRazorpayCustomerEmail();

  const providerName =
    this.selectedProvider?.name ||
    this.operatorDetails?.operatorName ||
    "";

  this.paymentProcessing = false;
  this.paymentSuccess = false;
  this.paymentStatusMessage = "";

  this.errorMessage = failureMessage;
  this.paymentFailed = true;

  /*
   * Full payload sent to SupportTicket_Create.
   */
  this.failedTicketData = {
    userId:
      this.resolveLoggedInUserId(),

    billerType,

    referenceId:
      paymentReference.masterId > 0
        ? paymentReference.masterId
        : null,

    orderId:
      orderId || null,

    prrn:
      paymentReference.prrn || null,

    amount:
      numericAmount,

    category:
      "Payment Failure",

    subCategory:
      "Payment Failed",

    priority:
      "Medium",

    assignedTo:
      null,

    subject:
      `${this.categoryName} payment failed`,

    description:
      [
        `Payment failed for ${this.categoryName}.`,

        customerName
          ? `Customer name: ${customerName}`
          : "",

        customerMobile
          ? `Mobile number: ${customerMobile}`
          : "",

        customerEmail
          ? `Email address: ${customerEmail}`
          : "",

        providerName
          ? `Provider: ${providerName}`
          : "",

        this.customerReference
          ? `Customer reference: ${this.customerReference}`
          : "",

        transactionId
          ? `Transaction ID: ${transactionId}`
          : "",

        orderId
          ? `Order ID: ${orderId}`
          : "",

        paymentReference.prrn
          ? `PRRN: ${paymentReference.prrn}`
          : "",

        `Reference ID: ${paymentReference.masterId}`,
        `Amount: ₹${numericAmount.toFixed(2)}`,
        `Failure reason: ${failureMessage}`,
      ]
        .filter(Boolean)
        .join("\n"),

    expectedResolutionOn:
      null,

    actionUser:
      this.resolvePaymentActionUser(),
  };

  /*
   * Masked data displayed in the dialog.
   */
  this.paymentFailureDialogData = {
    customerName,

    mobile:
      this.maskPaymentValue(
        customerMobile,
        4,
      ),

    email:
      this.maskPaymentEmail(
        customerEmail,
      ),

    serviceName:
      this.categoryName,

    providerName,

    customerReference:
      this.maskPaymentValue(
        this.customerReference,
        4,
      ),

    billerType,

    amount:
      numericAmount,

    referenceId:
      paymentReference.masterId > 0
        ? paymentReference.masterId
        : null,

    transactionId,

    orderId,

    prrn:
      paymentReference.prrn || "",

    failureReason:
      failureMessage,
  };

  this.showPaymentFailureDetails = false;

  this.querySubmitting = false;
  this.queryRaised = false;
  this.queryErrorMessage = "";
  this.createdQueryTicketNo = "";

  /*
   * Automatically open Payment Failed dialog.
   */
  this.paymentDialogMode = "failure";
  this.showPaymentFailureDialog = true;

  console.log(
    "Payment failure ticket payload:",
    this.failedTicketData,
  );
              });
            },
      });
}


  private completeRazorpayPayment(
    result: BillerOperatorSelectionDTO,
    paymentReference: BbpsResolvedPaymentReference,
    paymentResponse: any,
  ): void {
    this.paymentProcessing = true;
    this.paymentSuccess = false;
    this.paymentStatusMessage =
      "Payment successful. Saving transaction details…";
    this.errorMessage = "";
    this.informationMessage = "";

    const responseSources = [paymentResponse];

    const gatewayTransactionId = String(
      this.findFirstValueByAliases(
        responseSources,
        [
          "razorpay_payment_id",
          "razorpayPaymentId",
          "gatewayTransactionId",
          "paymentIdText",
          "payment_id",
        ],
      ) || "",
    ).trim();

    const gatewayOrderId = String(
      this.findFirstValueByAliases(
        responseSources,
        [
          "razorpay_order_id",
          "razorpayOrderId",
          "gatewayOrderId",
          "orderId",
          "order_id",
        ],
      ) || "",
    ).trim();

    const razorpaySignature = String(
      this.findFirstValueByAliases(
        responseSources,
        [
          "razorpay_signature",
          "razorpaySignature",
          "signature",
        ],
      ) || "",
    ).trim();

    Object.assign(result as any, {
      gatewayName: "RAZORPAY",
      paymentStatus: "Success",
      gatewayTransactionId,
      gatewayOrderId,
      razorpaySignature,
      prrn: paymentReference.prrn,
      masterId: paymentReference.masterId,
      masterType: paymentReference.masterType,
      billFetchId: paymentReference.billFetchId,
      paymentResponse,
    });

    this.savePaymentGatewayTransaction(
      result,
      paymentReference,
      paymentResponse,
      () => {
        this.finishSuccessfulPayment(
          result,
          paymentReference,
          paymentResponse,
        );
      },
    );
  }

  private savePaymentGatewayTransaction(
    result: BillerOperatorSelectionDTO,
    paymentReference: BbpsResolvedPaymentReference,
    paymentResponse: any,
    completedCallback: () => void,
  ): void {
    const payload = this.buildPaymentGatewayTransactionPayload(
      result,
      paymentReference,
      paymentResponse,
    );

    const paymentId = Number(
      this.fallbackPaymentIntentId ||
      this.findFirstValueByAliases(
        [paymentResponse],
        [
          "paymentId",
          "transactionId",
          "paymentGatewayTransactionId",
        ],
      ) ||
      0,
    );

    if (Number.isFinite(paymentId) && paymentId > 0) {
      const updatePayload: PaymentGatewayTransactionUpdateRequestDTO = {
        paymentId,
        moduleName: payload.moduleName,
        moduleId: payload.moduleId,
        gatewayName: payload.gatewayName,
        gatewayTransactionId: payload.gatewayTransactionId,
        gatewayOrderId: payload.gatewayOrderId,
        paymentFor: payload.paymentFor,
        requestedUserId: payload.requestedUserId,
        requestedAmount: payload.requestedAmount,
        clientPaidAmount: payload.clientPaidAmount,
        gatewayFeeAmount: payload.gatewayFeeAmount,
        disburseAmount: payload.disburseAmount,
        paymentStatus: payload.paymentStatus,
        currencyCode: payload.currencyCode,
        isRefunded: payload.isRefunded || 0,
        refundAmount: payload.refundAmount || 0,
        otherDetail1: payload.otherDetail1,
        otherDetail2: payload.otherDetail2,
        otherDetail3: payload.otherDetail3,
        otherDetail4: payload.otherDetail4,
        otherDetail5: payload.otherDetail5,
        otherDetail6: payload.otherDetail6,
        isActive: 1,
        actionUser: payload.actionUser,
      };

      this.paymentGatewayService
        .PaymentGatewayTransaction_Update(updatePayload)
        .subscribe({
          next: () => completedCallback(),
          error: () => {
            this.createPaymentGatewayTransaction(
              payload,
              completedCallback,
            );
          },
        });

      return;
    }

    this.createPaymentGatewayTransaction(
      payload,
      completedCallback,
    );
  }

  private createPaymentGatewayTransaction(
    payload: PaymentGatewayTransactionCreateRequestDTO,
    completedCallback: () => void,
  ): void {
    this.paymentGatewayService
      .PaymentGatewayTransaction_Create(payload)
      .subscribe({
        next: () => completedCallback(),
        error: (error: any) => {
          console.error(
            "BBPS PaymentGatewayTransaction save failed:",
            error,
          );

          /*
           * Razorpay payment is already successful. Do not block BBPS
           * finalization only because the secondary audit call failed.
           */
          this.informationMessage =
            "Payment succeeded. Transaction audit sync will be retried by the backend.";

          completedCallback();
        },
      });
  }

  private buildPaymentGatewayTransactionPayload(
    result: BillerOperatorSelectionDTO,
    paymentReference: BbpsResolvedPaymentReference,
    paymentResponse: any,
  ): PaymentGatewayTransactionCreateRequestDTO {
    const amount = Number(result.amount || this.amount || 0);
    const userId = this.resolveLoggedInUserId();

    const gatewayTransactionId = String(
      this.findFirstValueByAliases(
        [paymentResponse],
        [
          "razorpay_payment_id",
          "razorpayPaymentId",
          "gatewayTransactionId",
          "paymentIdText",
          "payment_id",
        ],
      ) || "",
    );

    const gatewayOrderId = String(
      this.findFirstValueByAliases(
        [paymentResponse],
        [
          "razorpay_order_id",
          "razorpayOrderId",
          "gatewayOrderId",
          "orderId",
          "order_id",
        ],
      ) || "",
    );

    const signature = String(
      this.findFirstValueByAliases(
        [paymentResponse],
        [
          "razorpay_signature",
          "razorpaySignature",
          "signature",
        ],
      ) || "",
    );

    return {
      moduleName: paymentReference.masterType,
      moduleId: paymentReference.masterId,
      gatewayName: "RAZORPAY",
      gatewayTransactionId,
      gatewayOrderId,
      paymentFor: `${this.categoryName} Payment`,
      requestedUserId: userId,
      requestedAmount: amount,
      clientPaidAmount: amount,
      gatewayFeeAmount: 0,
      disburseAmount: amount,
      paymentStatus: "Success",
      currencyCode: "INR",
      isRefunded: 0,
      refundAmount: 0,
      otherDetail1: "Selected in Razorpay Checkout",
      otherDetail2: "Web",
      otherDetail3: this.categoryName,
      otherDetail4: this.customerReference,
      otherDetail5: paymentReference.billFetchId,
      otherDetail6: signature || paymentReference.prrn,
      actionUser: this.resolvePaymentActionUser(),
    };
  }

private finishSuccessfulPayment(
  result: BillerOperatorSelectionDTO,
  paymentReference:
    BbpsResolvedPaymentReference,
  paymentResponse: any,
): void {
    this.clearRazorpayDismissMonitor();
  const transactionId = String(
    this.findFirstValueByAliases(
      [paymentResponse],
      [
        "razorpay_payment_id",
        "razorpayPaymentId",
        "gatewayTransactionId",
        "paymentIdText",
        "payment_id",
      ],
    ) || "",
  ).trim();

  const orderId = String(
    this.findFirstValueByAliases(
      [paymentResponse],
      [
        "razorpay_order_id",
        "razorpayOrderId",
        "gatewayOrderId",
        "orderId",
        "order_id",
      ],
    ) || "",
  ).trim();

  const customerName =
    this.getRazorpayCustomerName();

  const customerMobile =
    this.getRazorpayCustomerMobile();

  const customerEmail =
    this.getRazorpayCustomerEmail();

  const providerName =
    this.selectedProvider?.name ||
    this.operatorDetails?.operatorName ||
    "";

  const billerType =
    paymentReference.masterType ===
    "RECHARGE"
      ? "RECHARGE"
      : this.isCreditCardCategory
        ? "CREDITCARD"
        : "BBPS";

  this.paymentFailed = false;
  this.failedTicketData = null;

  this.paymentProcessing = false;
  this.paymentSuccess = true;

  this.paymentStatusMessage =
    "Payment completed successfully.";

  this.errorMessage = "";
  this.informationMessage = "";

  /*
   * Same expandable information used
   * by the payment-failure dialog.
   */
  this.paymentFailureDialogData = {
    customerName,

    mobile:
      this.maskPaymentValue(
        customerMobile,
        4,
      ),

    email:
      this.maskPaymentEmail(
        customerEmail,
      ),

    serviceName:
      this.categoryName,

    providerName,

    customerReference:
      this.maskPaymentValue(
        this.customerReference,
        4,
      ),

    billerType,

    amount:
      Number(
        result.amount ||
        this.amount ||
        0,
      ),

    referenceId:
      paymentReference.masterId > 0
        ? paymentReference.masterId
        : null,

    transactionId,

    orderId,

    prrn:
      paymentReference.prrn || "",

    /*
     * This property is also used as the
     * status message for successful payment.
     */
    failureReason:
      "Payment completed successfully.",
  };

  this.paymentDialogMode =
    "success";

  this.showPaymentFailureDetails =
    false;

  this.querySubmitting = false;
  this.queryRaised = false;
  this.queryErrorMessage = "";
  this.createdQueryTicketNo = "";

  /*
   * Open the success dialog.
   */
  this.showPaymentFailureDialog = true;

  this.persistPaymentSuccessForRefresh(
    result,
    paymentReference,
    paymentResponse,
  );

  this.paymentSucceeded.emit({
    selection:
      result,

    paymentReference,

    paymentResponse,
  });

  this.completed.emit(result);

  this.dispatchBbpsPaymentSuccessEvent(
    result,
    paymentReference,
    paymentResponse,
  );

    if (this.supportsPlanSelection) {
      this.transactionHistoryLoaded = false;

      if (this.transactionHistoryOpen) {
        this.loadRechargeTransactionHistory(true);
      }
    }

  /*
   * Do not refresh automatically here.
   * Refresh happens when Done is clicked.
   */
  this.clearPaymentRefreshTimer();

  document.body.style.overflow =
    "hidden";
}

  private syncLoggedInUserContext(): void {
    const sessionUserId = Number(
      (this.loggedInUser as any)?.userId || 0,
    );

    if (
      (!Number.isFinite(Number(this.userId)) || Number(this.userId) <= 0) &&
      Number.isFinite(sessionUserId) &&
      sessionUserId > 0
    ) {
      this.userId = sessionUserId;
    }

    if (!String(this.paymentActionUser || "").trim() && this.userId > 0) {
      this.paymentActionUser = String(this.userId);
    }
  }

  private resolveLoggedInUserId(): number {
    const inputUserId = Number(this.userId || 0);

    if (Number.isFinite(inputUserId) && inputUserId > 0) {
      return inputUserId;
    }

    const sessionUserId = Number(
      (this.loggedInUser as any)?.userId || 0,
    );

    if (Number.isFinite(sessionUserId) && sessionUserId > 0) {
      this.userId = sessionUserId;

      if (!String(this.paymentActionUser || "").trim()) {
        this.paymentActionUser = String(sessionUserId);
      }

      return sessionUserId;
    }

    return 0;
  }

  private resolvePaymentActionUser(): string {
    const actionUser = String(
      this.paymentActionUser ||
      this.resolveLoggedInUserId() ||
      "",
    ).trim();

    return actionUser || "SYSTEM";
  }

  private getPaymentReferenceSources(): any[] {
    return [
      this.rechargeInitializationResponse,
      this.fetchedBillApiResponse,
      (this.fetchedBillApiResponse as any)?.data,
      (this.fetchedBillApiResponse as any)?.result,
      (this.fetchedBillApiResponse as any)?.response,
      this.fetchedBill?.rawData,
      this.fetchedBill,
      this.selectedPlan?.rawData,
      this.selectedPlan,
      this.selectedProvider?.rawData,
      this.selectedProvider,
      this.operatorDetails,
      this.category,
      this.formData,
    ].filter(
      (source) =>
        source !== null &&
        source !== undefined,
    );
  }


  private findFirstValueByAliases(
    sources: any[],
    aliases: string[],
  ): any {
    const normalizedAliases = new Set(
      aliases.map((alias) =>
        this.normalizePaymentKey(alias),
      ),
    );

    for (const source of sources) {
      const value = this.findValueByAliasRecursive(
        source,
        normalizedAliases,
        0,
        new Set<any>(),
      );

      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }

    return null;
  }

  private findValueByAliasRecursive(
    value: any,
    aliases: Set<string>,
    depth: number,
    visited: Set<any>,
  ): any {
    if (
      value === null ||
      value === undefined ||
      depth > 8
    ) {
      return null;
    }

    const parsedValue = this.parsePossibleJson(value);

    if (
      parsedValue === null ||
      parsedValue === undefined ||
      typeof parsedValue !== "object"
    ) {
      return null;
    }

    if (visited.has(parsedValue)) {
      return null;
    }

    visited.add(parsedValue);

    if (Array.isArray(parsedValue)) {
      for (const item of parsedValue) {
        const nestedValue =
          this.findValueByAliasRecursive(
            item,
            aliases,
            depth + 1,
            visited,
          );

        if (
          nestedValue !== null &&
          nestedValue !== undefined &&
          String(nestedValue).trim() !== ""
        ) {
          return nestedValue;
        }
      }

      return null;
    }

    for (const [key, childValue] of Object.entries(parsedValue)) {
      if (
        aliases.has(
          this.normalizePaymentKey(key),
        ) &&
        childValue !== null &&
        childValue !== undefined &&
        String(childValue).trim() !== ""
      ) {
        return childValue;
      }
    }

    for (const childValue of Object.values(parsedValue)) {
      const nestedValue =
        this.findValueByAliasRecursive(
          childValue,
          aliases,
          depth + 1,
          visited,
        );

      if (
        nestedValue !== null &&
        nestedValue !== undefined &&
        String(nestedValue).trim() !== ""
      ) {
        return nestedValue;
      }
    }

    return null;
  }

  private normalizePaymentKey(value: string): string {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }

  private getSelectedPaymentModeName(): string {
    return (
      this.paymentModes.find(
        (mode) => mode.id === this.selectedPaymentMode,
      )?.name ||
      this.selectedPaymentMode ||
      "Online Payment"
    );
  }

  private getRazorpayCustomerName(): string {
    const sessionName = [
      (this.loggedInUser as any)?.firstName,
      (this.loggedInUser as any)?.middleName,
      (this.loggedInUser as any)?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return (
      this.fetchedBill?.customerName ||
      this.getFormValueByAliases([
        "customerName",
        "consumerName",
        "cardHolderName",
        "name",
      ]) ||
      sessionName ||
      (this.loggedInUser as any)?.userName ||
      (this.loggedInUser as any)?.username ||
      "Nishwik Customer"
    );
  }

  private getRazorpayCustomerEmail(): string {
    return (
      this.getFormValueByAliases([
        "email",
        "emailId",
        "customerEmail",
      ]) ||
      String(
        (this.loggedInUser as any)?.emailId ||
        (this.loggedInUser as any)?.email ||
        "",
      ).trim()
    );
  }

  private getRazorpayCustomerMobile(): string {
    const mobile =
      this.mobileNumber ||
      this.getFormValueByAliases([
        "mobile",
        "mobileNo",
        "phone",
        "phoneNumber",
        "contact",
      ]);

    const sessionMobile = String(
      (this.loggedInUser as any)?.mobileNo ||
      (this.loggedInUser as any)?.mobileNumber ||
      (this.loggedInUser as any)?.contact ||
      "",
    );

    return String(mobile || sessionMobile || "")
      .replace(/\D/g, "")
      .slice(-10);
  }

  private getFormValueByAliases(
    aliases: string[],
  ): string {
    const normalizedAliases = new Set(
      aliases.map((alias) =>
        this.normalizePaymentKey(alias),
      ),
    );

    for (const [key, value] of Object.entries(this.formData)) {
      if (
        normalizedAliases.has(
          this.normalizePaymentKey(key),
        )
      ) {
        const text = String(value ?? "").trim();

        if (text) {
          return text;
        }
      }
    }

    return "";
  }

  private getRazorpayErrorMessage(error: any): string {
    const source = String(error?.source || "")
      .trim()
      .toUpperCase();

    const statusCode = Number(
      error?.statusCode ||
      error?.status ||
      error?.error?.statusCode ||
      error?.error?.status ||
      0,
    );

    const apiMessage =
      error?.error?.message ||
      error?.error?.Message ||
      error?.message ||
      error?.Message ||
      String(error || "").replace(/^Error:\s*/i, "") ||
      "";

    if (source === "CHECKOUT") {
      return apiMessage ||
        "Payment was cancelled or could not be completed.";
    }

    if (statusCode === 400) {
      return apiMessage ||
        "The payment request is invalid. Please verify the bill details.";
    }

    if (statusCode === 401 || statusCode === 403) {
      return "The payment request is not authorized. Please sign in again.";
    }

    if (statusCode === 404) {
      return "The Razorpay payment service is currently unavailable.";
    }

    if (statusCode === 429) {
      return "Too many payment attempts. Please wait and try again.";
    }

    return apiMessage ||
      "Unable to start Razorpay payment. Please try again.";
  }

  private persistPaymentSuccessForRefresh(
    result: BillerOperatorSelectionDTO,
    paymentReference: BbpsResolvedPaymentReference,
    paymentResponse: any,
  ): void {
    try {
      const gatewayOrderId = String(
        this.findFirstValueByAliases(
          [paymentResponse],
          [
            "razorpay_order_id",
            "razorpayOrderId",
            "gatewayOrderId",
            "orderId",
            "order_id",
          ],
        ) || "",
      );

      const gatewayPaymentId = String(
        this.findFirstValueByAliases(
          [paymentResponse],
          [
            "razorpay_payment_id",
            "razorpayPaymentId",
            "gatewayTransactionId",
            "paymentIdText",
            "payment_id",
          ],
        ) || "",
      );

      localStorage.setItem(
        this.getPaymentRefreshStorageKey(),
        JSON.stringify({
          createdAt: Date.now(),
          userId: this.userId,
          categoryId: this.category?.categoryId ?? 0,
          categoryName: this.categoryName,
          customerReference: this.customerReference,
          amount: Number(this.amount ?? 0),
          paymentMode: this.selectedPaymentMode,
          masterId: paymentReference.masterId,
          masterType: paymentReference.masterType,
          prrn: paymentReference.prrn,
          billFetchId: paymentReference.billFetchId,
          gatewayOrderId,
          gatewayPaymentId,
          selection: result,
        }),
      );
    } catch {
      /* Storage may be blocked in private mode. Payment success still continues. */
    }
  }


  private getPaymentRefreshStorageKey(): string {
    return [
      "nishwik-bbps-payment-success",
      this.userId || 0,
      this.category?.categoryId || 0,
      this.customerReference || "customer",
    ].join("-");
  }

  private dispatchBbpsPaymentSuccessEvent(
    result: BillerOperatorSelectionDTO,
    paymentReference: BbpsResolvedPaymentReference,
    paymentResponse: any,
  ): void {
    try {
      window.dispatchEvent(
        new CustomEvent(
          "nishwik:bbps-payment-success",
          {
            detail: {
              selection: result,
              paymentReference,
              paymentResponse,
            },
          },
        ),
      );
    } catch {
      /* Custom events are an enhancement; the completed output still fires. */
    }
  }

  private schedulePaymentAutoRefresh(): void {
    this.clearPaymentRefreshTimer();

    if (!this.autoRefreshAfterPayment) {
      return;
    }

    this.paymentRefreshTimer = setTimeout(() => {
      window.location.reload();
    }, 4000);
  }

  private clearPaymentRefreshTimer(): void {
    if (this.paymentRefreshTimer) {
      clearTimeout(this.paymentRefreshTimer);
      this.paymentRefreshTimer = null;
    }
  }

  private unsubscribeRazorpayRequest(): void {
    if (this.razorpaySubscription) {
      this.razorpaySubscription.unsubscribe?.();
      this.razorpaySubscription = null;
    }
  }

  // goBack(): void {
  //   if (this.paymentProcessing) {
  //     return;
  //   }

  //   this.errorMessage = "";
  //   this.informationMessage = "";

  //   if (
  //     this.step === "customer-details" &&
  //     this.usesExternalProviderList &&
  //     this.providerStage ===
  //       "customer-form"
  //   ) {
  //     this.editSelectedProvider();
  //     return;
  //   }

  //   if (this.step === "review") {
  //     this.step = "customer-details";

  //     if (
  //       this.usesExternalProviderList &&
  //       this.selectedProviderCode
  //     ) {
  //       this.providerStage =
  //         "customer-form";
  //     }

  //     this.operatorDetails = null;
  //     this.prepaidPlans = [];
  //     this.selectedPlan = null;
  //     this.selectedPlanCategory = "";
  //     this.planSearch = "";
  //     this.fetchedBill = null;
  //     this.fetchedBillApiResponse = null;
  //     this.rechargeInitializationResponse = null;
  //     this.resetFallbackPaymentIntent();
  //     this.amount = null;
  //   }
  // }

  goBack(): void {
  if (this.paymentProcessing) {
    return;
  }

  this.errorMessage = "";
  this.informationMessage = "";

  if (
    this.step === "customer-details" &&
    this.usesExternalProviderList &&
    this.providerStage ===
      "customer-form"
  ) {
    this.editSelectedProvider();
    return;
  }

  if (this.step === "review") {

    this.stopPaymentCountdown();

    this.step = "customer-details";

    if (
      this.usesExternalProviderList &&
      this.selectedProviderCode
    ) {
      this.providerStage =
        "customer-form";
    }

    this.operatorDetails = null;
    this.prepaidPlans = [];
    this.selectedPlan = null;
    this.selectedPlanCategory = "";
    this.planSearch = "";
    this.fetchedBill = null;
    this.fetchedBillApiResponse = null;
    this.rechargeInitializationResponse = null;
    this.resetFallbackPaymentIntent();
    this.amount = null;
  }
}

  selectPaymentMode(modeId: string): void {
    if (this.paymentProcessing) {
      return;
    }

    this.selectedPaymentMode = modeId;
    this.errorMessage = "";
    this.informationMessage = "";
  }

  getFieldError(
    field: BillerInputFieldDTO,
  ): string {
    if (!this.submitted) {
      return "";
    }

    const value = String(
      this.formData[field.key] ?? "",
    ).trim();

    if (field.required && !value) {
      return `${field.label} is required.`;
    }

    if (
      value &&
      field.minLength &&
      value.length < field.minLength
    ) {
      return (
        `${field.label} must contain at least ` +
        `${field.minLength} characters.`
      );
    }

    if (
      value &&
      field.maxLength &&
      value.length > field.maxLength
    ) {
      return (
        `${field.label} cannot exceed ` +
        `${field.maxLength} characters.`
      );
    }

    if (
      value &&
      field.pattern &&
      !this.matchesPattern(value, field.pattern)
    ) {
      return `Please enter a valid ${field.label.toLowerCase()}.`;
    }

    return "";
  }

  getInputIcon(field: BillerInputFieldDTO): string {
    if (field.type === "tel") {
      return "fa-solid fa-mobile-screen-button";
    }

    if (field.type === "email") {
      return "fa-solid fa-envelope";
    }

    if (field.type === "date") {
      return "fa-solid fa-calendar-days";
    }

    if (
      field.key.toLowerCase().includes("vehicle")
    ) {
      return "fa-solid fa-car";
    }

    if (
      field.key.toLowerCase().includes("policy")
    ) {
      return "fa-solid fa-shield-halved";
    }

    if (
      field.key.toLowerCase().includes("card")
    ) {
      return "fa-regular fa-credit-card";
    }

    return "fa-regular fa-id-card";
  }

  getDisplayValue(
    field: BillerInputFieldDTO,
  ): string {
    const value = String(
      this.formData[field.key] ?? "",
    ).trim();

    if (!value) {
      return "-";
    }

    const lowerKey = field.key.toLowerCase();

    if (
      lowerKey.includes("creditcard") ||
      lowerKey.includes("cardnumber")
    ) {
      if (value.length <= 4) {
        return value;
      }

      return `${"*".repeat(value.length - 4)}${value.slice(-4)}`;
    }

    return value;
  }

  getPlanCostPerDay(
    plan: PrepaidPlanViewDTO,
  ): number | null {
    const validityDays = Number(
      plan.validity.match(/\d+/)?.[0] ?? 0,
    );

    if (
      !validityDays ||
      !Number.isFinite(validityDays)
    ) {
      return null;
    }

    return plan.amount / validityDays;
  }

  trackByFieldKey(
    index: number,
    field: BillerInputFieldDTO,
  ): string {
    return field.key;
  }

  trackByPaymentMode(
    index: number,
    mode: BillerPaymentModeDTO,
  ): string {
    return mode.id;
  }

  trackByProviderCode(
    index: number,
    provider: BbpsOperatorItemDTO,
  ): string {
    return provider.code;
  }

  trackByPlanId(
    index: number,
    plan: PrepaidPlanViewDTO,
  ): string {
    return plan.id;
  }

  private validateFields(): boolean {
    for (const field of this.fields) {
      if (this.getFieldError(field)) {
        this.errorMessage =
          "Please correct the highlighted details.";
        return false;
      }
    }

    return true;
  }

  private matchesPattern(
    value: string,
    pattern: string,
  ): boolean {
    try {
      return new RegExp(pattern).test(value);
    } catch {
      return true;
    }
  }

  private normalizeOperatorResponse(
    response:
      | MobileOperatorApiResponseDTO
      | MobileOperatorResponseDTO,
  ): NormalizedMobileOperatorDTO {
    const envelope =
      response as MobileOperatorApiResponseDTO;

    const source =
      envelope?.data ??
      envelope?.Data ??
      (response as MobileOperatorResponseDTO) ??
      {};

    const info =
      source?.info ??
      source?.Info ??
      {};

    const envelopeSucceeded =
      envelope?._success ??
      envelope?.success ??
      envelope?.Success;

    const envelopeMessage =
      envelope?.message ??
      envelope?.Message ??
      "";

    const responseError =
      source?.err_msg ??
      source?.ErrMsg ??
      "";

    return {
      operatorName:
        info?.opr ??
        info?.Opr ??
        "",

      operatorCode:
        source?.operatorCode ??
        source?.OperatorCode ??
        "",

      circleName:
        info?.Circle ??
        info?.circle ??
        "",

      /**
       * Provider CircleCode is used for GetPrepaidPlane.
       * MobileCircleCode remains available separately.
       */
      circleCode:
        info?.CircleCode ??
        info?.circleCode ??
        source?.mobileCircleCode ??
        source?.MobileCircleCode ??
        "",

      mobileCircleCode:
        source?.mobileCircleCode ??
        source?.MobileCircleCode ??
        "",

      status:
        source?.status ??
        source?.Status ??
        "",

      errorMessage:
        responseError ||
        (
          envelopeSucceeded === false
            ? envelopeMessage
            : ""
        ),
    };
  }

  private normalizeProviderList(
    response: BbpsOperatorApiResponseDTO,
  ): BbpsOperatorItemDTO[] {
    const parsedResponse: any =
      this.parsePossibleJson(response);

    const parsedData: any =
      this.parsePossibleJson(
        parsedResponse?.data ??
        parsedResponse?.Data ??
        parsedResponse?.result ??
        parsedResponse?.Result ??
        parsedResponse?.response ??
        parsedResponse?.Response ??
        parsedResponse,
      );

    /*
     * Different BBPS endpoints return provider lists in different locations.
     *
     * Existing biller endpoints commonly use:
     *   { items: [...] }
     *
     * Credit-card GetOperatorList currently uses:
     *   { msg: "Operator Fetched.", data: [...] }
     *
     * The previous implementation checked parsedData.items but never accepted
     * parsedData itself when it was already an array. That is why the Network
     * response contained banks while the UI displayed zero providers.
     */
    const directCandidates: any[] = [
      parsedResponse?.items,
      parsedResponse?.Items,
      parsedResponse?.operators,
      parsedResponse?.Operators,
      parsedResponse?.operatorList,
      parsedResponse?.OperatorList,
      parsedResponse?.providers,
      parsedResponse?.Providers,
      parsedResponse?.providerList,
      parsedResponse?.ProviderList,

      parsedData?.items,
      parsedData?.Items,
      parsedData?.operators,
      parsedData?.Operators,
      parsedData?.operatorList,
      parsedData?.OperatorList,
      parsedData?.providers,
      parsedData?.Providers,
      parsedData?.providerList,
      parsedData?.ProviderList,

      parsedData,
    ];

    let items: any[] = [];

    for (const candidate of directCandidates) {
      const parsedCandidate =
        this.parsePossibleJson(candidate);

      if (Array.isArray(parsedCandidate)) {
        items = parsedCandidate;
        break;
      }
    }

    /*
     * Keep compatibility with irregular nested provider envelopes.
     * This only runs when no direct array was found.
     */
    if (items.length === 0) {
      const collectedItems: any[] = [];

      this.collectProviderCandidates(
        parsedData,
        collectedItems,
        0,
      );

      if (parsedData !== parsedResponse) {
        this.collectProviderCandidates(
          parsedResponse,
          collectedItems,
          0,
        );
      }

      items = collectedItems;
    }

    const normalizedProviders =
      items
        .map((rawItem: any) =>
          this.parsePossibleJson(rawItem),
        )
        .filter(
          (item: any) =>
            item !== null &&
            item !== undefined,
        )
        .filter((item: any) => {
          if (typeof item === "string") {
            return !!item.trim();
          }

          const activeValue =
            item?.isActive ??
            item?.IsActive ??
            item?.active ??
            item?.Active ??
            item?.status ??
            item?.Status ??
            1;

          const normalizedActive =
            String(activeValue)
              .trim()
              .toLowerCase();

          return ![
            "0",
            "false",
            "inactive",
            "deleted",
            "disabled",
          ].includes(normalizedActive);
        })
        .map(
          (
            item: any,
            index: number,
          ): BbpsOperatorItemDTO => {
            if (typeof item === "string") {
              const providerName =
                item.trim();

              return {
                id: String(index),
                code: String(index),
                name: providerName,
                rawData: item,
              };
            }

            const operatorName = String(
              item?.operatorName ??
              item?.OperatorName ??
              item?.oprName ??
              item?.OprName ??
              item?.operator ??
              item?.Operator ??
              item?.billerName ??
              item?.BillerName ??
              item?.providerName ??
              item?.ProviderName ??
              item?.serviceProviderName ??
              item?.ServiceProviderName ??
              item?.subCategoryName ??
              item?.SubCategoryName ??
              item?.name ??
              item?.Name ??
              "Service Provider",
            ).trim();

            const providerCode = String(
              item?.oprCode ??
              item?.OprCode ??
              item?.OPRCode ??
              item?.operatorCode ??
              item?.OperatorCode ??
              item?.spKey ??
              item?.SpKey ??
              item?.SPKey ??
              item?.spkey ??
              item?.SPKEY ??
              item?.serviceProviderKey ??
              item?.ServiceProviderKey ??
              item?.operatorKey ??
              item?.OperatorKey ??
              item?.billerCode ??
              item?.BillerCode ??
              item?.providerCode ??
              item?.ProviderCode ??
              item?.code ??
              item?.Code ??
              "",
            ).trim();

            const providerId = String(
              item?.subCategoryId ??
              item?.SubCategoryId ??
              item?.operatorId ??
              item?.OperatorId ??
              item?.billerId ??
              item?.BillerId ??
              item?.providerId ??
              item?.ProviderId ??
              item?.id ??
              item?.Id ??
              providerCode ??
              index,
            ).trim();

            return {
              id:
                providerId ||
                providerCode ||
                String(index),

              code:
                providerCode ||
                providerId ||
                String(index),

              name: operatorName,

              logo:
                item?.billerLogo ??
                item?.BillerLogo ??
                item?.operatorLogo ??
                item?.OperatorLogo ??
                item?.providerLogo ??
                item?.ProviderLogo ??
                item?.logo ??
                item?.Logo ??
                undefined,

              rawData: item,
            };
          },
        )
        .filter(
          (provider) =>
            !!provider.name &&
            !!provider.code,
        );

    /*
     * Prevent duplicate cards when a recursive fallback encounters the same
     * provider through more than one nested envelope.
     */
    const uniqueProviders =
      new Map<string, BbpsOperatorItemDTO>();

    normalizedProviders.forEach(
      (provider) => {
        const uniqueKey = [
          String(provider.code)
            .trim()
            .toLowerCase(),
          String(provider.name)
            .trim()
            .toLowerCase(),
        ].join("|");

        if (!uniqueProviders.has(uniqueKey)) {
          uniqueProviders.set(
            uniqueKey,
            provider,
          );
        }
      },
    );

    return Array.from(
      uniqueProviders.values(),
    ).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  private normalizeFetchedBill(
    response: BbpsBillApiResponseDTO,
  ): BbpsBillDetailDTO | null {
    /*
     * Start from the complete response instead of only response.data. Several
     * BBPS providers return the actual bill as JSON inside responseJson.
     */
    const source =
      this.findBillObject(
        response,
        0,
      );

    if (!source) {
      return null;
    }

    const amount =
      this.readNumericValue(
        this.readValueByAliases(
          source,
          [
            "payamount",
            "payAmount",
            "payableAmount",
            "paymentAmount",
            "billedamount",
            "billedAmount",
            "billAmount",
            "amount",
            "totalAmount",
            "dueAmount",
            "netAmount",
            "grossAmount",
          ],
        ),
      );

    const customerName =
      this.readStringByAliases(
        source,
        [
          "customerName",
          "consumerName",
          "subscriberName",
          "accountHolderName",
          "cardHolderName",
          "name",
        ],
      );

    const billNumber =
      this.readStringByAliases(
        source,
        [
          "billNumber",
          "billNo",
          "invoiceNumber",
          "invoiceNo",
          "billId",
          "invoiceId",
        ],
      );

    const billDate =
      this.readStringByAliases(
        source,
        [
          "billDate",
          "invoiceDate",
          "billingDate",
          "generatedOn",
          "billGeneratedDate",
        ],
      );

    const dueDate =
      this.readStringByAliases(
        source,
        [
          "dueDate",
          "paymentDueDate",
          "billDueDate",
          "expiryDate",
        ],
      );

    const status =
      this.readStringByAliases(
        source,
        [
          "billStatus",
          "paymentStatus",
          "status",
        ],
      );

    const hasCustomerFacingData =
      amount > 0 ||
      !!customerName ||
      !!billNumber ||
      !!billDate ||
      !!dueDate ||
      !!this.readStringByAliases(
        source,
        [
          "customerNumber",
          "consumerNumber",
          "subscriberId",
          "accountNumber",
          "consumerId",
          "caNumber",
          "vehicleNumber",
          "policyNumber",
          "loanNumber",
        ],
      );

    if (!hasCustomerFacingData) {
      return null;
    }

    return {
      customerName,
      billNumber,
      billDate,
      dueDate,
      amount,
      status,
      rawData: source,
    };
  }

  private isFetchBillResponseSuccessful(
    response: BbpsBillApiResponseDTO,
  ): boolean {
    const bill =
      this.normalizeFetchedBill(response);

    const status = String(
      this.findFirstValueByAliases(
        [response],
        [
          "billFetchStatus",
          "responseStatus",
          "status",
        ],
      ) || "",
    )
      .trim()
      .toUpperCase();

    if (
      status &&
      [
        "FAILED",
        "FAILURE",
        "ERROR",
        "INVALID",
        "REJECTED",
      ].includes(status)
    ) {
      return false;
    }

    /*
     * Some BBPS wrappers return a misleading root success flag or numeric
     * message even though responseJson/data.data contains a valid bill.
     * The nested bill is the source of truth when usable bill data exists.
     */
    if (bill) {
      const hasUsableBill =
        bill.amount > 0 ||
        !!bill.customerName ||
        !!bill.billNumber ||
        !!bill.billDate ||
        !!bill.dueDate ||
        this.buildBillDisplayRows(
          response,
          bill,
        ).length > 0;

      if (hasUsableBill) {
        return true;
      }
    }

    const explicitSuccess =
      response?._success ??
      response?.success ??
      response?.Success;

    return explicitSuccess === true;
  }

  private getFetchBillFailureMessage(
    response: any,
    fallbackMessage: string,
  ): string {
    const candidates = [
      this.findFirstValueByAliases(
        [response],
        [
          "errorMessage",
          "errMessage",
          "errorDescription",
          "description",
          "userMessage",
          "message",
          "msg",
        ],
      ),
      response?.message,
      response?.Message,
    ];

    for (const candidate of candidates) {
      const text = String(
        candidate ?? "",
      ).trim();

      if (
        !text ||
        /^(?:200|201|202|204|ok|success)$/i.test(text)
      ) {
        continue;
      }

      return text;
    }

    return fallbackMessage;
  }

  private getFetchBillHttpErrorMessage(
    error: any,
  ): string {
    const nestedMessage =
      this.getFetchBillFailureMessage(
        error?.error ?? error,
        "",
      );

    if (nestedMessage) {
      return nestedMessage;
    }

    const statusCode = Number(
      error?.status ??
      error?.statusCode ??
      0,
    );

    if (
      statusCode >= 200 &&
      statusCode < 300
    ) {
      return "The biller returned an incomplete bill response. Please verify the customer number and try again.";
    }

    return (
      this.getApiErrorMessage(error) ||
      "Unable to fetch the bill. Please verify the customer details and try again."
    );
  }

  private buildBillDisplayRows(
    response: BbpsBillApiResponseDTO,
    bill: BbpsBillDetailDTO | null,
  ): BbpsBillDisplayRow[] {
    const source =
      bill?.rawData ??
      this.findBillObject(response, 0);

    if (
      !source ||
      typeof source !== "object"
    ) {
      return [];
    }

    const rows:
      BbpsBillDisplayRow[] = [];

    const addedKeys =
      new Set<string>();

    const addRow = (
      key: string,
      label: string,
      rawValue: any,
    ): void => {
      if (
        rawValue === null ||
        rawValue === undefined ||
        typeof rawValue === "object"
      ) {
        return;
      }

      const value =
        this.formatBillDisplayValue(
          key,
          rawValue,
        );

      if (
        !value ||
        value.toLowerCase() === "null" ||
        value.toLowerCase() === "undefined"
      ) {
        return;
      }

      const normalizedKey =
        this.normalizeObjectKey(key);

      if (
        !normalizedKey ||
        addedKeys.has(normalizedKey)
      ) {
        return;
      }

      addedKeys.add(normalizedKey);

      rows.push({
        key: normalizedKey,
        label,
        value,
      });
    };

    const preferredFields: Array<{
      key: string;
      label: string;
      aliases: string[];
    }> = [
      {
        key: "customerName",
        label: "Customer Name",
        aliases: [
          "customerName",
          "consumerName",
          "subscriberName",
          "accountHolderName",
          "name",
        ],
      },
      {
        key: "customerNumber",
        label: "Customer / Consumer Number",
        aliases: [
          "customerNumber",
          "consumerNumber",
          "consumerNo",
          "subscriberId",
          "subscriberNumber",
          "accountNumber",
          "accountId",
          "consumerId",
          "caNumber",
          "kNumber",
          "loanNumber",
          "policyNumber",
          "vehicleNumber",
        ],
      },
      {
        key: "billNumber",
        label: "Bill Number",
        aliases: [
          "billNumber",
          "billNo",
          "invoiceNumber",
          "invoiceNo",
          "billId",
        ],
      },
      {
        key: "billDate",
        label: "Bill Date",
        aliases: [
          "billDate",
          "invoiceDate",
          "billingDate",
          "generatedOn",
        ],
      },
      {
        key: "dueDate",
        label: "Due Date",
        aliases: [
          "dueDate",
          "paymentDueDate",
          "billDueDate",
          "expiryDate",
        ],
      },
      {
        key: "billPeriod",
        label: "Bill Period",
        aliases: [
          "billPeriod",
          "billingPeriod",
          "billMonth",
          "billingMonth",
        ],
      },
      {
        key: "billedAmount",
        label: "Billed Amount",
        aliases: [
          "billedamount",
          "billedAmount",
          "billAmount",
          "totalAmount",
          "dueAmount",
        ],
      },
      {
        key: "payAmount",
        label: "Payable Amount",
        aliases: [
          "payamount",
          "payAmount",
          "payableAmount",
          "paymentAmount",
          "netAmount",
        ],
      },
      {
        key: "status",
        label: "Bill Status",
        aliases: [
          "billStatus",
          "paymentStatus",
          "status",
        ],
      },
      {
        key: "mobile",
        label: "Registered Mobile",
        aliases: [
          "mobile",
          "mobileNo",
          "registeredMobile",
          "customerMobile",
        ],
      },
    ];

    preferredFields.forEach(
      (field) => {
        const value =
          this.findFirstValueByAliases(
            [source],
            field.aliases,
          );

        addRow(
          field.key,
          field.label,
          value,
        );
      },
    );

    this.flattenBillDisplayRows(
      source,
      rows,
      addedKeys,
      0,
    );

    return rows.slice(0, 30);
  }

  private flattenBillDisplayRows(
    source: any,
    rows: BbpsBillDisplayRow[],
    addedKeys: Set<string>,
    depth: number,
    parentLabel = "",
  ): void {
    if (
      source === null ||
      source === undefined ||
      depth > 3 ||
      rows.length >= 30
    ) {
      return;
    }

    const parsedSource =
      this.parsePossibleJson(source);

    if (Array.isArray(parsedSource)) {
      parsedSource.forEach(
        (item, index) => {
          if (
            item &&
            typeof item === "object" &&
            !Array.isArray(item)
          ) {
            const itemLabel =
              this.readStringByAliases(
                item,
                [
                  "label",
                  "name",
                  "key",
                  "fieldName",
                  "parameterName",
                  "description",
                ],
              );

            const itemValue =
              this.readValueByAliases(
                item,
                [
                  "value",
                  "fieldValue",
                  "parameterValue",
                  "data",
                  "amount",
                ],
              );

            if (
              itemLabel &&
              itemValue !== undefined &&
              typeof itemValue !== "object"
            ) {
              const key =
                this.normalizeObjectKey(
                  itemLabel,
                );

              if (
                key &&
                !addedKeys.has(key) &&
                this.shouldDisplayBillField(key)
              ) {
                const value =
                  this.formatBillDisplayValue(
                    key,
                    itemValue,
                  );

                if (value) {
                  addedKeys.add(key);

                  rows.push({
                    key,
                    label:
                      this.formatBillFieldLabel(
                        itemLabel,
                      ),
                    value,
                  });
                }
              }

              return;
            }
          }

          this.flattenBillDisplayRows(
            item,
            rows,
            addedKeys,
            depth + 1,
            parentLabel ||
              `Detail ${index + 1}`,
          );
        },
      );

      return;
    }

    if (
      typeof parsedSource !== "object"
    ) {
      return;
    }

    Object.entries(parsedSource).forEach(
      ([key, rawValue]) => {
        if (rows.length >= 30) {
          return;
        }

        const normalizedKey =
          this.normalizeObjectKey(key);

        if (
          !normalizedKey ||
          !this.shouldDisplayBillField(
            normalizedKey,
          )
        ) {
          return;
        }

        const parsedValue =
          this.parsePossibleJson(rawValue);

        if (
          parsedValue !== null &&
          typeof parsedValue === "object"
        ) {
          this.flattenBillDisplayRows(
            parsedValue,
            rows,
            addedKeys,
            depth + 1,
            this.formatBillFieldLabel(key),
          );

          return;
        }

        if (
          addedKeys.has(normalizedKey)
        ) {
          return;
        }

        const value =
          this.formatBillDisplayValue(
            normalizedKey,
            parsedValue,
          );

        if (!value) {
          return;
        }

        addedKeys.add(normalizedKey);

        const ownLabel =
          this.formatBillFieldLabel(key);

        rows.push({
          key: normalizedKey,
          label:
            parentLabel &&
            ![
              "data",
              "details",
              "billdetails",
              "additionalinfo",
              "additionaldetails",
            ].includes(
              this.normalizeObjectKey(
                parentLabel,
              ),
            )
              ? `${parentLabel} - ${ownLabel}`
              : ownLabel,
          value,
        });
      },
    );
  }

  private shouldDisplayBillField(
    normalizedKey: string,
  ): boolean {
    const hiddenKeys =
      new Set<string>([
        "success",
        "_success",
        "message",
        "responsemessage",
        "responsejson",
        "requestid",
        "bbpsrequestid",
        "prrn",
        "billfetchid",
        "billfetchreference",
        "transactionid",
        "paymentid",
        "masterid",
        "operatorcode",
        "billercode",
        "spkey",
        "subcategoryid",
        "categoryid",
        "acceptpayment",
        "error",
        "errormessage",
        "errorcode",
        "responsecode",
        "statuscode",
        "rawdata",
      ]);

    return !hiddenKeys.has(normalizedKey);
  }

  private formatBillFieldLabel(
    key: string,
  ): string {
    return String(key || "")
      .replace(/[_-]+/g, " ")
      .replace(
        /([a-z0-9])([A-Z])/g,
        "$1 $2",
      )
      .replace(/\s+/g, " ")
      .trim()
      .replace(
        /\b\w/g,
        (character) =>
          character.toUpperCase(),
      );
  }

  private formatBillDisplayValue(
    key: string,
    rawValue: any,
  ): string {
    if (
      rawValue === null ||
      rawValue === undefined
    ) {
      return "";
    }

    if (typeof rawValue === "boolean") {
      return rawValue ? "Yes" : "No";
    }

    const text =
      String(rawValue).trim();

    if (
      !text ||
      text.toLowerCase() === "null" ||
      text.toLowerCase() === "undefined"
    ) {
      return "";
    }

    const normalizedKey =
      this.normalizeObjectKey(key);

    if (
      normalizedKey.includes("amount") ||
      normalizedKey.includes("charge") ||
      normalizedKey.includes("balance")
    ) {
      const numeric =
        this.readNumericValue(text);

      if (
        Number.isFinite(numeric) &&
        numeric >= 0
      ) {
        return `₹${numeric.toFixed(2)}`;
      }
    }

    return text;
  }

  private getSessionMobileForBbps(): string {
    const mobile =
      this.getFormValueByAliases([
        "mobile",
        "mobileNo",
        "mobileNumber",
        "registeredMobile",
        "phone",
        "contact",
      ]) ||
      String(
        (this.loggedInUser as any)?.mobileNo ||
        (this.loggedInUser as any)?.mobileNumber ||
        (this.loggedInUser as any)?.contact ||
        "",
      );

    return mobile
      .replace(/\D/g, "")
      .slice(-10);
  }

  private getProviderSubCategoryId(): number {
    const providerId =
      Number(
        this.selectedProvider?.id || 0,
      );

    if (
      Number.isFinite(providerId) &&
      providerId > 0
    ) {
      return providerId;
    }

    const rawProvider =
      this.selectedProvider?.rawData as any;

    const subCategoryId =
      Number(
        rawProvider?.subCategoryId ??
        rawProvider?.SubCategoryId ??
        rawProvider?.id ??
        rawProvider?.Id ??
        0,
      );

    return Number.isFinite(
      subCategoryId,
    )
      ? subCategoryId
      : 0;
  }

  private normalizeCustomerReferenceForFetchBill(
    value: string,
  ): string {
    const source =
      String(value || "").trim();

    if (!source) {
      return "";
    }

    const categoryName =
      this.categoryName.toLowerCase();

    if (
      categoryName.includes("fastag") ||
      categoryName.includes("challan")
    ) {
      return source
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    }

    /*
     * Preserve provider-required letters and symbols, for example
     * MCGM Water customer number KW@0447195.
     * Only numeric-only utility references are left-padded to 12 digits.
     */
    const isNumericOnly =
      /^\d+$/.test(source);

    const requiresTwelveDigitPadding =
      categoryName.includes("electricity") ||
      categoryName.includes("water") ||
      categoryName.includes("gas") ||
      categoryName.includes("lpg");

    if (
      requiresTwelveDigitPadding &&
      isNumericOnly &&
      source.length < 12
    ) {
      return source.padStart(
        12,
        "0",
      );
    }

    return source;
  }

  private normalizePrepaidPlans(
    response: PrepaidPlanApiResponseDTO,
  ): PrepaidPlanViewDTO[] {
    const root = this.parsePossibleJson(
      response?.data ??
      response?.Data ??
      response?.result ??
      response?.Result ??
      response,
    );

    const candidates: Array<{
      category: string;
      source: any;
    }> = [];

    this.collectPlanCandidates(
      root,
      "Recommended",
      candidates,
      0,
    );

    const uniquePlans =
      new Map<string, PrepaidPlanViewDTO>();

    candidates.forEach((candidate, index) => {
      const source =
        this.parsePossibleJson(candidate.source);

      if (
        !source ||
        typeof source !== "object" ||
        Array.isArray(source)
      ) {
        return;
      }

      const amount = this.readPlanAmount(source);

      if (
        !Number.isFinite(amount) ||
        amount <= 0
      ) {
        return;
      }

      const category = this.formatPlanCategory(
        this.readStringByAliases(
          source,
          [
            "category",
            "categoryName",
            "planCategory",
            "planType",
            "packType",
            "rechargeType",
            "group",
            "groupName",
            "type",
          ],
        ) ||
        candidate.category ||
        "Recommended",
      );

      const validity =
        this.readStringByAliases(
          source,
          [
            "validity",
            "validityDescription",
            "validityText",
            "validityPeriod",
            "validityDays",
            "days",
          ],
        ) ||
        "-";

      const description =
        this.readStringByAliases(
          source,
          [
            "description",
            "planDescription",
            "planDesc",
            "details",
            "detail",
            "desc",
            "remarks",
            "offerDescription",
          ],
        ) ||
        "";

      const dataBenefit =
        this.readStringByAliases(
          source,
          [
            "dataBenefit",
            "dataAllowance",
            "dataValue",
            "packBenefit",
            "benefits",
            "benefit",
            "offer",
            "data",
          ],
        ) ||
        description ||
        "-";

      const talkTime =
        this.readStringByAliases(
          source,
          [
            "talkTime",
            "talktime",
            "talkValue",
            "talktimeValue",
            "talk_time",
          ],
        ) ||
        "";

      const id =
        this.readStringByAliases(
          source,
          [
            "planId",
            "planCode",
            "rechargeId",
            "packId",
            "id",
          ],
        ) ||
        `${category}-${amount}-${validity}-${index}`;

      const uniqueKey = [
        category,
        amount,
        validity,
        dataBenefit,
        description,
      ].join("|");

      if (!uniquePlans.has(uniqueKey)) {
        uniquePlans.set(uniqueKey, {
          id,
          category,
          amount,
          validity,
          dataBenefit,
          description,
          talkTime,
          rawData: source,
        });
      }
    });

    return Array.from(
      uniquePlans.values(),
    ).sort((a, b) => a.amount - b.amount);
  }

  private collectPlanCandidates(
    value: any,
    categoryHint: string,
    output: Array<{
      category: string;
      source: any;
    }>,
    depth: number,
  ): void {
    if (
      value === null ||
      value === undefined ||
      depth > 10
    ) {
      return;
    }

    const parsedValue =
      this.parsePossibleJson(value);

    if (Array.isArray(parsedValue)) {
      parsedValue.forEach((item) => {
        const parsedItem =
          this.parsePossibleJson(item);

        if (this.looksLikePlan(parsedItem)) {
          output.push({
            category: categoryHint,
            source: parsedItem,
          });

          return;
        }

        this.collectPlanCandidates(
          parsedItem,
          categoryHint,
          output,
          depth + 1,
        );
      });

      return;
    }

    if (
      typeof parsedValue !== "object"
    ) {
      return;
    }

    if (this.looksLikePlan(parsedValue)) {
      output.push({
        category: categoryHint,
        source: parsedValue,
      });

      return;
    }

    Object.entries(parsedValue).forEach(
      ([key, childValue]) => {
        const parsedChild =
          this.parsePossibleJson(childValue);

        const amountFromKey =
          this.readNumericValue(key);

        const keyLooksLikeAmount =
          /^\s*(?:₹|rs\.?\s*)?\d+(?:\.\d+)?\s*$/i
            .test(key) &&
          amountFromKey > 0;

        if (keyLooksLikeAmount) {
          if (
            parsedChild &&
            typeof parsedChild === "object" &&
            !Array.isArray(parsedChild)
          ) {
            output.push({
              category: categoryHint,
              source: {
                ...parsedChild,
                amount:
                  this.readPlanAmount(parsedChild) ||
                  amountFromKey,
              },
            });

            return;
          }

          if (
            typeof parsedChild === "string"
          ) {
            output.push({
              category: categoryHint,
              source: {
                amount: amountFromKey,
                description: parsedChild,
              },
            });

            return;
          }
        }

        const nextCategory =
          this.isGenericPlanContainerKey(key)
            ? categoryHint
            : this.formatPlanCategory(key);

        this.collectPlanCandidates(
          parsedChild,
          nextCategory,
          output,
          depth + 1,
        );
      },
    );
  }

  private collectProviderCandidates(
    value: any,
    output: any[],
    depth: number,
  ): void {
    if (
      value === null ||
      value === undefined ||
      depth > 10
    ) {
      return;
    }

    const parsedValue =
      this.parsePossibleJson(value);

    if (Array.isArray(parsedValue)) {
      parsedValue.forEach((item) => {
        const parsedItem =
          this.parsePossibleJson(item);

        if (
          typeof parsedItem === "string" &&
          parsedItem.trim()
        ) {
          output.push(parsedItem.trim());
          return;
        }

        this.collectProviderCandidates(
          parsedItem,
          output,
          depth + 1,
        );
      });

      return;
    }

    if (
      typeof parsedValue !== "object"
    ) {
      return;
    }

    const providerName =
      this.readStringByAliases(
        parsedValue,
        [
          "billerName",
          "operatorName",
          "providerName",
          "serviceProviderName",
          "displayName",
          "companyName",
          "serviceName",
          "operatorDescription",
          "operatorDesc",
          "providerDescription",
          "label",
          "operator",
          "opr",
          "biller",
          "name",
        ],
      );

    if (providerName) {
      output.push(parsedValue);
      return;
    }

    const ignoredKeys = new Set([
      "success",
      "_success",
      "status",
      "message",
      "error",
      "errormessage",
      "responsecode",
      "statuscode",
      "data",
      "result",
      "response",
      "items",
      "operators",
      "providers",
      "billerlist",
      "operatorlist",
      "providerlist",
    ]);

    Object.entries(parsedValue).forEach(
      ([key, childValue]) => {
        const parsedChild =
          this.parsePossibleJson(childValue);

        if (
          parsedChild &&
          (
            typeof parsedChild === "object" ||
            Array.isArray(parsedChild)
          )
        ) {
          this.collectProviderCandidates(
            parsedChild,
            output,
            depth + 1,
          );
          return;
        }

        const normalizedKey =
          this.normalizeObjectKey(key);

        if (
          ignoredKeys.has(normalizedKey) ||
          parsedChild === null ||
          parsedChild === undefined
        ) {
          return;
        }

        const primitiveValue =
          String(parsedChild).trim();

        if (!primitiveValue) {
          return;
        }

        /*
         * Some biller APIs return a dictionary:
         * {
         *   "MSEDCL": "Maharashtra State Electricity..."
         * }
         * or the reverse:
         * {
         *   "Maharashtra State Electricity...": "MSEDCL"
         * }
         */
        const keyLooksLikeCode =
          /^[A-Z0-9_-]{2,20}$/.test(key);

        const valueLooksLikeCode =
          /^[A-Z0-9_-]{2,20}$/.test(
            primitiveValue,
          );

        if (
          keyLooksLikeCode &&
          !valueLooksLikeCode
        ) {
          output.push({
            providerCode: key,
            providerName:
              primitiveValue,
          });
          return;
        }

        if (
          !keyLooksLikeCode &&
          valueLooksLikeCode
        ) {
          output.push({
            providerCode:
              primitiveValue,
            providerName: key,
          });
        }
      },
    );
  }

  private collectObjectCandidates(
    value: any,
    output: any[],
    depth: number,
  ): void {
    if (
      value === null ||
      value === undefined ||
      depth > 7
    ) {
      return;
    }

    const parsedValue = this.parsePossibleJson(value);

    if (Array.isArray(parsedValue)) {
      parsedValue.forEach((item) => {
        this.collectObjectCandidates(
          item,
          output,
          depth + 1,
        );
      });

      return;
    }

    if (typeof parsedValue !== "object") {
      return;
    }

    const hasName =
      parsedValue?.billerName ??
      parsedValue?.BillerName ??
      parsedValue?.operatorName ??
      parsedValue?.OperatorName ??
      parsedValue?.name ??
      parsedValue?.Name;

    const hasCode =
      parsedValue?.billerCode ??
      parsedValue?.BillerCode ??
      parsedValue?.operatorCode ??
      parsedValue?.OperatorCode ??
      parsedValue?.code ??
      parsedValue?.Code ??
      parsedValue?.id ??
      parsedValue?.Id;

    if (hasName && hasCode) {
      output.push(parsedValue);
      return;
    }

    Object.values(parsedValue).forEach((child) => {
      this.collectObjectCandidates(
        child,
        output,
        depth + 1,
      );
    });
  }

  private findBillObject(
    value: any,
    depth: number,
  ): any | null {
    if (
      value === null ||
      value === undefined ||
      depth > 10
    ) {
      return null;
    }

    const parsedValue =
      this.parsePossibleJson(value);

    if (Array.isArray(parsedValue)) {
      for (const item of parsedValue) {
        const found =
          this.findBillObject(
            item,
            depth + 1,
          );

        if (found) {
          return found;
        }
      }

      return null;
    }

    if (
      typeof parsedValue !== "object"
    ) {
      return null;
    }

    const amountValue =
      this.readValueByAliases(
        parsedValue,
        [
          "payamount",
          "payAmount",
          "payableAmount",
          "paymentAmount",
          "billedamount",
          "billedAmount",
          "billAmount",
          "amount",
          "totalAmount",
          "dueAmount",
          "netAmount",
        ],
      );

    const customerIdentity =
      this.readValueByAliases(
        parsedValue,
        [
          "customerName",
          "consumerName",
          "subscriberName",
          "customerNumber",
          "consumerNumber",
          "subscriberId",
          "accountNumber",
          "consumerId",
          "caNumber",
          "vehicleNumber",
          "policyNumber",
          "loanNumber",
        ],
      );

    const billIdentity =
      this.readValueByAliases(
        parsedValue,
        [
          "billNumber",
          "billNo",
          "invoiceNumber",
          "invoiceNo",
          "billDate",
          "dueDate",
          "billPeriod",
        ],
      );

    const looksLikeBill =
      amountValue !== undefined ||
      customerIdentity !== undefined ||
      billIdentity !== undefined;

    if (looksLikeBill) {
      return parsedValue;
    }

    for (
      const child of
      Object.values(parsedValue)
    ) {
      const found =
        this.findBillObject(
          child,
          depth + 1,
        );

      if (found) {
        return found;
      }
    }

    return null;
  }

  private looksLikePlan(value: any): boolean {
    if (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value)
    ) {
      return false;
    }

    return this.readPlanAmount(value) > 0;
  }

  private readPlanAmount(source: any): number {
    const rawAmount =
      this.readValueByAliases(
        source,
        [
          "amount",
          "amt",
          "rs",
          "price",
          "mrp",
          "rechargeAmount",
          "planAmount",
          "packAmount",
          "planPrice",
          "amountInRs",
          "denomination",
          "denom",
          "rechargeValue",
          "planValue",
        ],
      );

    return this.readNumericValue(rawAmount);
  }

  private readNumericValue(value: any): number {
    const normalized = String(value ?? "")
      .replace(/,/g, "")
      .replace(/[^\d.]/g, "");

    return Number(normalized || 0);
  }

  private normalizeObjectKey(
    value: string,
  ): string {
    return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }

  private readValueByAliases(
    source: any,
    aliases: string[],
  ): any {
    if (
      !source ||
      typeof source !== "object" ||
      Array.isArray(source)
    ) {
      return undefined;
    }

    const normalizedEntries =
      new Map<string, any>();

    Object.entries(source).forEach(
      ([key, value]) => {
        normalizedEntries.set(
          this.normalizeObjectKey(key),
          value,
        );
      },
    );

    for (const alias of aliases) {
      const value = normalizedEntries.get(
        this.normalizeObjectKey(alias),
      );

      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }

    return undefined;
  }

  private readStringByAliases(
    source: any,
    aliases: string[],
  ): string {
    const value = this.readValueByAliases(
      source,
      aliases,
    );

    if (
      value === undefined ||
      value === null
    ) {
      return "";
    }

    if (
      typeof value === "object"
    ) {
      return "";
    }

    return String(value).trim();
  }

  private parsePossibleJson(value: any): any {
    let parsedValue = value;

    for (
      let attempt = 0;
      attempt < 4;
      attempt++
    ) {
      if (
        typeof parsedValue !== "string"
      ) {
        break;
      }

      const trimmedValue =
        parsedValue.trim();

      if (!trimmedValue) {
        break;
      }

      let candidate =
        trimmedValue;

      /*
       * Some providers return responseJson with text before/after the JSON.
       * Extract the first complete object/array without changing the original
       * FetchBill envelope.
       */
      if (
        !candidate.startsWith("{") &&
        !candidate.startsWith("[") &&
        !candidate.startsWith('"')
      ) {
        const objectStart =
          candidate.indexOf("{");
        const objectEnd =
          candidate.lastIndexOf("}");

        const arrayStart =
          candidate.indexOf("[");
        const arrayEnd =
          candidate.lastIndexOf("]");

        if (
          objectStart >= 0 &&
          objectEnd > objectStart
        ) {
          candidate =
            candidate.slice(
              objectStart,
              objectEnd + 1,
            );
        } else if (
          arrayStart >= 0 &&
          arrayEnd > arrayStart
        ) {
          candidate =
            candidate.slice(
              arrayStart,
              arrayEnd + 1,
            );
        } else {
          break;
        }
      }

      try {
        parsedValue =
          JSON.parse(candidate);
      } catch {
        break;
      }
    }

    return parsedValue;
  }

  private isGenericPlanContainerKey(
    key: string,
  ): boolean {
    return [
      "data",
      "result",
      "response",
      "plans",
      "plan",
      "items",
      "records",
      "prepaidplans",
      "planlist",
      "plandata",
      "rechargeplans",
      "operatorplans",
      "packs",
      "packlist",
      "responsebody",
      "payload",
      "info",
      "details",
    ].includes(
      key.toLowerCase().replace(/[^a-z0-9]/g, ""),
    );
  }

  private formatPlanCategory(
    value: any,
  ): string {
    const source = String(
      value ?? "Recommended",
    ).trim();

    if (!source) {
      return "Recommended";
    }

    const normalizedKey = source
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

    const categoryMap: Record<string, string> = {
      RECOMMENDED: "Recommended",
      POPULAR: "Recommended",
      COMBO: "Recommended",
      UNLIMITED: "Truly Unlimited",
      TRULYUNLIMITED: "Truly Unlimited",
      SMARTRECHARGE: "Smart Recharge",
      DATA: "Data",
      DATAPACK: "Data",
      FULLTT: "Full Talktime",
      FULLTALKTIME: "Full Talktime",
      TOPUP: "Top Up",
      ROAMING: "Roaming",
      SMS: "SMS",
      ISD: "ISD",
      FIVEG: "Unlimited 5G Plans",
      UNLIMITED5G: "Unlimited 5G Plans",
    };

    if (categoryMap[normalizedKey]) {
      return categoryMap[normalizedKey];
    }

    return source
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(
        /\b\w/g,
        (character) => character.toUpperCase(),
      );
  }

  private firstNonEmpty(
    ...values: any[]
  ): string {
    for (const value of values) {
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        return String(value).trim();
      }
    }

    return "";
  }

  private getApiErrorMessage(error: any): string {
    const responseBody = error?.error;

    if (
      typeof responseBody === "string" &&
      responseBody.trim()
    ) {
      return responseBody;
    }

    return (
      responseBody?.err_msg ??
      responseBody?.ErrMsg ??
      responseBody?.message ??
      responseBody?.Message ??
      responseBody?.error?.message ??
      responseBody?.error?.Message ??
      error?.message ??
      "Unable to complete the request. Please try again."
    );
  }

  private getFieldsForCategory():
    BillerInputFieldDTO[] {
    const name = this.categoryName.toLowerCase();

    if (
      name === "prepaid" ||
      name.includes("mobile prepaid")
    ) {
      return [
        {
          key: "mobileNumber",
          label: "Mobile Number",
          placeholder: "Enter 10-digit mobile number",
          type: "tel",
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: "^[6-9][0-9]{9}$",
        },
      ];
    }

    if (name.includes("datacard")) {
      return [
        {
          key: "accountNumber",
          label: "Datacard Number",
          placeholder: "Enter datacard number",
          type: "tel",
          required: true,
          minLength: 8,
          maxLength: 20,
          pattern: "^[0-9]+$",
        },
      ];
    }

    if (name.includes("postpaid")) {
      return [
        {
          key: "mobileNumber",
          label: "Postpaid Mobile Number",
          placeholder: "Enter 10-digit mobile number",
          type: "tel",
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: "^[6-9][0-9]{9}$",
        },
      ];
    }

    if (name.includes("landline")) {
      return [
        {
          key: "stdCode",
          label: "STD Code",
          placeholder: "Enter STD code",
          type: "tel",
          required: true,
          minLength: 2,
          maxLength: 6,
          pattern: "^[0-9]+$",
        },
        {
          key: "landlineNumber",
          label: "Landline Number",
          placeholder: "Enter landline number",
          type: "tel",
          required: true,
          minLength: 6,
          maxLength: 15,
          pattern: "^[0-9]+$",
        },
      ];
    }

    if (
      name.includes("broadband") ||
      name.includes("cable") ||
      name.includes("dth")
    ) {
      return [
        {
          key: "subscriberId",
          label: "Subscriber ID / Customer ID",
          placeholder:
            "Enter subscriber ID or customer ID",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
      ];
    }

    if (name.includes("electricity")) {
      return [
        {
          key: "consumerNumber",
          label: "Consumer Number",
          placeholder:
            "Enter electricity consumer number",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
      ];
    }

    if (
      name.includes("gas") ||
      name.includes("lpg")
    ) {
      return [
        {
          key: "consumerId",
          label: "Consumer ID",
          placeholder: "Enter gas consumer ID",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
      ];
    }

    if (name.includes("water")) {
      return [
        {
          key: "consumerNumber",
          label: "Consumer Number",
          placeholder:
            "Enter water consumer number",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
      ];
    }

    if (name.includes("insurance")) {
      return [
        {
          key: "policyNumber",
          label: "Policy Number",
          placeholder: "Enter policy number",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
        {
          key: "dateOfBirth",
          label: "Date of Birth",
          placeholder: "Select date of birth",
          type: "date",
          required: false,
        },
      ];
    }

    if (name.includes("emi")) {
      return [
        {
          key: "loanAccountNumber",
          label: "Loan Account Number",
          placeholder: "Enter loan account number",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 35,
        },
      ];
    }

    if (name.includes("challan")) {
      return [
        {
          key: "vehicleNumber",
          label: "Vehicle Number",
          placeholder: "Example: MH01AB1234",
          type: "text",
          required: true,
          minLength: 6,
          maxLength: 15,
        },
        {
          key: "challanNumber",
          label: "Challan Number",
          placeholder:
            "Enter challan number, if available",
          type: "text",
          required: false,
          maxLength: 30,
        },
      ];
    }

    if (name.includes("fastag")) {
      return [
        {
          key: "vehicleNumber",
          label: "Vehicle Number",
          placeholder: "Example: MH01AB1234",
          type: "text",
          required: true,
          minLength: 6,
          maxLength: 15,
        },
      ];
    }

    if (name.includes("municipality")) {
      return [
        {
          key: "propertyId",
          label: "Property ID",
          placeholder: "Enter property ID",
          type: "text",
          required: true,
          minLength: 4,
          maxLength: 30,
        },
      ];
    }

    if (name.includes("credit card")) {
      return [
        {
          key: "creditCardNumber",
          label: "Credit Card Number",
          placeholder: "Enter credit card number",
          type: "tel",
          required: true,
          minLength: 12,
          maxLength: 19,
          pattern: "^[0-9]+$",
        },
        {
          key: "mobileNumber",
          label: "Registered Mobile Number",
          placeholder:
            "Enter registered mobile number",
          type: "tel",
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: "^[6-9][0-9]{9}$",
        },
      ];
    }

    if (name.includes("voucher")) {
      return [
        {
          key: "mobileNumber",
          label: "Mobile Number",
          placeholder: "Enter mobile number",
          type: "tel",
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: "^[6-9][0-9]{9}$",
        },
        {
          key: "emailAddress",
          label: "Email Address",
          placeholder: "Enter email address",
          type: "email",
          required: false,
        },
      ];
    }

    return [
      {
        key: "customerNumber",
        label: "Customer ID / Account Number",
        placeholder:
          "Enter customer ID or account number",
        type: "text",
        required: true,
        minLength: 4,
        maxLength: 35,
      },
    ];
  }
closePaymentFailureDialog(): void {
  if (this.querySubmitting) {
    return;
  }

  this.showPaymentFailureDialog = false;
}

raisePaymentQuery(): void {
  if (
    !this.failedTicketData ||
    this.querySubmitting ||
    this.queryRaised
  ) {
    return;
  }

  this.querySubmitting = true;
  this.queryErrorMessage = "";
  this.createdQueryTicketNo = "";

  console.log(
    "SupportTicket_Create payload:",
    this.failedTicketData,
  );

  this.supportTicketService
    .SupportTicket_Create(
      this.failedTicketData,
    )
    .subscribe({
      next: (response: any) => {
        const apiSuccess =
          response?._success ??
          response?.success ??
          response?.Success ??
          true;

        if (apiSuccess === false) {
          this.querySubmitting = false;

          this.queryErrorMessage =
            response?.message ||
            response?.Message ||
            "Unable to raise the query.";

          return;
        }

        const createdTicket =
          response?.data?.data ||
          response?.Data?.Data ||
          response?.data ||
          response?.Data ||
          response;

        this.createdQueryTicketNo = String(
          createdTicket?.ticketNo ??
          createdTicket?.TicketNo ??
          createdTicket?.ticketNumber ??
          createdTicket?.TicketNumber ??
          "",
        );

        this.querySubmitting = false;
        this.queryRaised = true;

        console.log(
          "Support query created:",
          response,
        );
      },

      error: (error: any) => {
        console.error(
          "SupportTicket_Create failed:",
          error,
        );

        this.querySubmitting = false;

        this.queryErrorMessage =
          error?.error?.message ||
          error?.error?.Message ||
          error?.message ||
          "Unable to raise the query. Please try again.";
      },
    });
}

private maskPaymentValue(
  value: string,
  visibleCharacters = 4,
): string {
  const source =
    String(value || "").trim();

  if (!source) {
    return "";
  }

  if (
    source.length <= visibleCharacters
  ) {
    return source;
  }

  return `${"*".repeat(
    source.length - visibleCharacters,
  )}${source.slice(-visibleCharacters)}`;
}

private maskPaymentEmail(
  value: string,
): string {
  const email =
    String(value || "").trim();

  if (!email || !email.includes("@")) {
    return email;
  }

  const [username, domain] =
    email.split("@");

  const maskedUsername =
    username.length <= 1
      ? "*"
      : `${username.charAt(0)}${"*".repeat(
          Math.max(
            username.length - 1,
            3,
          ),
        )}`;

  return `${maskedUsername}@${domain}`;
}

donePaymentSuccessDialog(): void {
  this.showPaymentFailureDialog = false;
  this.showPaymentFailureDetails = false;
  this.paymentDialogMode = null;

  this.restorePageScroll();
  if (this.autoRefreshAfterPayment) {
    window.location.reload();
    return;
  }

  this.closed.emit();
}

get shouldShowPaymentTimer(): boolean {
  const categoryName = this.categoryName
    .trim()
    .toLowerCase();

  const isMobileCategory =
    this.supportsPlanSelection ||
    categoryName === "prepaid" ||
    categoryName.includes("mobile prepaid") ||
    categoryName.includes("mobile recharge") ||
    categoryName.includes("datacard prepaid") ||
    categoryName.includes("postpaid") ||
    categoryName.includes("mobile postpaid") ||
    categoryName.includes("datacard postpaid");

  return (
    !this.isCreditCardCategory &&
    !isMobileCategory
  );
}

get paymentTimerText(): string {
  const safeSeconds = Math.max(
    0,
    Number(this.paymentTimerSeconds || 0)
  );

  const minutes = Math.floor(
    safeSeconds / 60
  );

  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(
    2,
    "0"
  )}`;
}

private startPaymentCountdown(): void {
  this.stopPaymentCountdown();

  this.paymentTimerSeconds = 600;
  this.paymentTimerExpired = false;

  if (!this.shouldShowPaymentTimer) {
    return;
  }

  this.paymentCountdownExpiresAt =
    Date.now() + 10 * 60 * 1000;

  this.paymentCountdownInterval =
    setInterval(() => {
      const remainingMilliseconds =
        this.paymentCountdownExpiresAt -
        Date.now();

      const remainingSeconds = Math.max(
        0,
        Math.ceil(
          remainingMilliseconds / 1000
        )
      );

      this.paymentTimerSeconds =
        remainingSeconds;

      if (remainingSeconds <= 0) {
        this.paymentTimerExpired = true;

        this.stopPaymentCountdown(
          false
        );

        this.paymentStatusMessage =
          "Payment session time has expired.";

        this.changeDetectorRef
          .markForCheck();

        this.changeDetectorRef
          .detectChanges();

        return;
      }

      this.changeDetectorRef
        .markForCheck();

      this.changeDetectorRef
        .detectChanges();
    }, 1000);
}

private stopPaymentCountdown(
  resetTimer: boolean = true
): void {
  if (this.paymentCountdownInterval) {
    clearInterval(
      this.paymentCountdownInterval
    );

    this.paymentCountdownInterval =
      null;
  }

  this.paymentCountdownExpiresAt = 0;

  if (resetTimer) {
    this.paymentTimerSeconds = 600;
    this.paymentTimerExpired = false;
  }
}
}